import { transformError as sdkTransformError } from '@wix/sdk-runtime/transform-error';
import {
  renameKeysFromSDKRequestToRESTRequest,
  renameKeysFromRESTResponseToSDKResponse,
} from '@wix/sdk-runtime/rename-all-nested-keys';
import { HttpClient, NonNullablePaths } from '@wix/sdk-types';
import * as ambassadorWixDataV2DataCollection from './data-v2-data-collection-collections.http.js';

/** A data collection determines the structure of data to be stored in a database. */
export interface DataCollection {
  /**
   * [Collection ID](https://dev.wix.com/docs/develop-websites-sdk/code-your-site/work-with-data/introduction/about-collections#collection-ids). For example, `my-first-collection`.
   * For [app collections](https://dev.wix.com/docs/api-reference/business-solutions/cms/collection-management/wix-app-collections/introduction), use `AppName/CollectionName`.
   * @immutable
   * @maxLength 256
   */
  _id?: string;
  /**
   * Collection type. Indicates how the collection was created and how it is stored.
   * @readonly
   */
  collectionType?: CollectionTypeWithLiterals;
  /**
   * ID of the app that defined this collection. For collections defined by Wix users, this value is null.
   * @format GUID
   * @readonly
   */
  ownerAppId?: string | null;
  /**
   * Maximum number of items returned in a single query, based on the underlying storage.
   * Native collections have a maximum page size of 1000 for offset-based queries or 100 for cursor-based queries.
   * External collections' maximum page size defaults to 50, but an external provider can set any maximum value up to 1000.
   * @readonly
   */
  maxPageSize?: number | null;
  /**
   * Collection's display name as shown in the CMS. For example, `My First Collection`.
   * @maxLength 1000
   */
  displayName?: string | null;
  /**
   * Default item sorting order when a query doesn't specify one.
   * @readonly
   */
  defaultDisplayOrder?: Sort;
  /**
   * UI-friendly namespace of the Wix app with which the data collection is associated, such as Stores or Bookings.
   * Empty for all data collections not owned by Wix apps.
   * @readonly
   * @maxLength 1000
   */
  displayNamespace?: string | null;
  /**
   * Field whose value the CMS displays to represent the collection item when referenced in a different collection.
   * @maxLength 1000
   */
  displayField?: string | null;
  /**
   * Capabilities the collection supports.
   * Capabilities are set by default. Values depend on the collection type.
   * @readonly
   */
  capabilities?: CollectionCapabilities;
  /**
   * Collection's field structure.
   * @maxSize 1000
   */
  fields?: Field[];
  /** [Permissions](https://dev.wix.com/docs/api-reference/business-solutions/cms/collection-management/data-collections/introduction#permissions) required to access and modify collection items, defined by the lowest role needed to perform each action. */
  permissions?: Permissions;
  /**
   * Collection's current revision number, which increments each time the collection is updated. For an update operation to succeed, you must specify the latest revision number.
   * @readonly
   */
  revision?: string | null;
  /**
   * Plugins the collection uses. Plugins apply additional capabilities to the collection or extend its functionality.
   * @maxSize 100
   */
  plugins?: Plugin[];
  /**
   * Paging modes the collection supports. In native collections, offset-based paging is supported by default.
   * @readonly
   * @maxSize 2
   */
  pagingModes?: PagingModeWithLiterals[];
  /**
   * Date the collection was created.
   * @readonly
   */
  _createdDate?: Date | null;
  /**
   * Date the collection was last updated.
   * @readonly
   */
  _updatedDate?: Date | null;
  /**
   * [Permissions](https://dev.wix.com/docs/api-reference/business-solutions/cms/collection-management/data-collections/introduction#permissions) required to access and modify collection items, defined by the lowest access level required to perform each action.
   * @internal
   */
  dataPermissions?: DataPermissions;
  /**
   * Actual data operations permitted for the caller. Included only if requested by `include_allowed_data_permissions`
   * @internal
   * @readonly
   */
  allowedDataPermissions?: AllowedDataPermissions;
  /**
   * Free-text description of the collection's purpose and contents.
   * @internal
   * @maxLength 4000
   */
  description?: string | null;
}

export enum CollectionType {
  /** User-created collection. */
  NATIVE = 'NATIVE',
  /** [Collection](https://support.wix.com/en/article/velo-working-with-wix-app-collections-and-code#what-are-wix-app-collections) created by a Wix app when it is installed. This type of collection can be modified dynamically by that app (for example, Wix Forms). */
  WIX_APP = 'WIX_APP',
  /** Collection created by a Wix Blocks app. */
  BLOCKS_APP = 'BLOCKS_APP',
  /** Collection located in externally connected storage. */
  EXTERNAL = 'EXTERNAL',
}

/** @enumType */
export type CollectionTypeWithLiterals =
  | CollectionType
  | 'NATIVE'
  | 'WIX_APP'
  | 'BLOCKS_APP'
  | 'EXTERNAL';

export interface Sort {
  /**
   * Field to sort by.
   * @maxLength 1000
   */
  fieldKey?: string;
  /**
   * Sort order. Use `ASC` for ascending order or `DESC` for descending order.
   *
   * Default: `ASC`
   */
  direction?: DirectionWithLiterals;
}

export enum Direction {
  ASC = 'ASC',
  DESC = 'DESC',
}

/** @enumType */
export type DirectionWithLiterals = Direction | 'ASC' | 'DESC';

export interface CollectionCapabilities {
  /**
   * Data operations the collection supports. The listed operations can be performed on data the collection contains.
   *
   * > **Note**: The `PATCH` and `BULK_PATCH` operations aren't currently supported.
   * @maxSize 100
   */
  dataOperations?: DataOperationWithLiterals[];
  /**
   * Collection operations supported. The listed operations can be performed on the collection itself.
   * @maxSize 100
   */
  collectionOperations?: CollectionOperationWithLiterals[];
  /** Maximum number of indexes for the collection. */
  indexLimits?: IndexLimits;
  /**
   * Maximum number of indexes for the collection.
   * @internal
   */
  indexQuotaLimits?: IndexQuotaLimits;
}

export enum DataOperation {
  AGGREGATE = 'AGGREGATE',
  BULK_INSERT = 'BULK_INSERT',
  BULK_REMOVE = 'BULK_REMOVE',
  BULK_SAVE = 'BULK_SAVE',
  BULK_UPDATE = 'BULK_UPDATE',
  COUNT = 'COUNT',
  DISTINCT = 'DISTINCT',
  FIND = 'FIND',
  GET = 'GET',
  INSERT = 'INSERT',
  INSERT_REFERENCE = 'INSERT_REFERENCE',
  IS_REFERENCED = 'IS_REFERENCED',
  QUERY_REFERENCED = 'QUERY_REFERENCED',
  REMOVE = 'REMOVE',
  REMOVE_REFERENCE = 'REMOVE_REFERENCE',
  REPLACE_REFERENCES = 'REPLACE_REFERENCES',
  SAVE = 'SAVE',
  TRUNCATE = 'TRUNCATE',
  UPDATE = 'UPDATE',
  PATCH = 'PATCH',
  BULK_PATCH = 'BULK_PATCH',
}

/** @enumType */
export type DataOperationWithLiterals =
  | DataOperation
  | 'AGGREGATE'
  | 'BULK_INSERT'
  | 'BULK_REMOVE'
  | 'BULK_SAVE'
  | 'BULK_UPDATE'
  | 'COUNT'
  | 'DISTINCT'
  | 'FIND'
  | 'GET'
  | 'INSERT'
  | 'INSERT_REFERENCE'
  | 'IS_REFERENCED'
  | 'QUERY_REFERENCED'
  | 'REMOVE'
  | 'REMOVE_REFERENCE'
  | 'REPLACE_REFERENCES'
  | 'SAVE'
  | 'TRUNCATE'
  | 'UPDATE'
  | 'PATCH'
  | 'BULK_PATCH';

export enum CollectionOperation {
  /** Allows updating the collection's structure, for example adding, updating, or deleting fields. If not included, the collection's structure can't be changed. */
  UPDATE = 'UPDATE',
  /** Allows deleting the entire collection. If not included, the collection can't be deleted. */
  REMOVE = 'REMOVE',
}

/** @enumType */
export type CollectionOperationWithLiterals =
  | CollectionOperation
  | 'UPDATE'
  | 'REMOVE';

export interface IndexLimits {
  /** Maximum number of regular (non-unique) indexes allowed for this collection. */
  regular?: number;
  /** Maximum number of unique indexes allowed for this collection. */
  unique?: number;
  /** Maximum number of regular and unique indexes allowed for this collection. */
  total?: number;
}

export interface IndexQuotaLimits {
  /**
   * limit of regular single-field indexes, even if 0 1-field indices may be created using
   * 3-field quota (if available)
   */
  regular1Field?: number;
  /** limit of regular indexes up to 3-fields (in addition to 1-field indexes quota) */
  regular3Field?: number;
  /** limit of unique indexes */
  unique1Field?: number;
  /** Maximum number of regular and unique indexes allowed for this collection. */
  total?: number;
}

export interface Field extends FieldRangeValidationsOneOf {
  /** Range of possible values for a numerical field. */
  numberRange?: NumberRange;
  /** Length range permitted for a text field. Relevant for fields that hold strings, such as those of type `TEXT` or `RICH_TEXT`. */
  stringLengthRange?: StringLengthRange;
  /** Array size range permitted. Relevant for fields that hold arrays, such as those of type `ARRAY`, `ARRAY_STRING`, or `ARRAY_DOCUMENT`. */
  arraySizeRange?: ArraySizeRange;
  /**
   * Unique identifier for the field. For example, `firstName`.
   * @maxLength 1000
   */
  key?: string;
  /**
   * Field's display name when displayed in the CMS. For example, `First Name`.
   * @maxLength 1000
   */
  displayName?: string | null;
  /**
   * Field's data type.
   *
   * Learn more about [data types in Wix Data](https://dev.wix.com/docs/rest/business-solutions/cms/data-types-in-wix-data).
   */
  type?: TypeWithLiterals;
  /** Metadata for complex data types. This property only exists for references, multi-references, objects, and arrays. */
  typeMetadata?: TypeMetadata;
  /**
   * Whether the field is a system field.
   * @readonly
   */
  systemField?: boolean;
  /**
   * Capabilities the field supports.
   * @readonly
   */
  capabilities?: FieldCapabilities;
  /** Whether the field is encrypted. */
  encrypted?: boolean;
  /**
   * Field description.
   * @maxLength 16000
   */
  description?: string | null;
  /**
   * Whether the field is read-only. A read-only field can't be changed.
   *
   * Default: `false`
   */
  readOnly?: boolean | null;
  /**
   * Whether the field is immutable. An immutable field can be set once, but then cannot be updated.
   *
   * Default: `false`
   */
  immutable?: boolean | null;
  /**
   * Whether the field is required.
   *
   * Default: `false`
   */
  required?: boolean | null;
  /**
   * Additional optional plugins for the field.
   * @maxSize 100
   */
  plugins?: FieldPlugin[];
  /**
   * This indicates that this fields is conditional and is not retrieved by default.
   * @readonly
   */
  condition?: FieldCondition;
}

/** @oneof */
export interface FieldRangeValidationsOneOf {
  /** Range of possible values for a numerical field. */
  numberRange?: NumberRange;
  /** Length range permitted for a text field. Relevant for fields that hold strings, such as those of type `TEXT` or `RICH_TEXT`. */
  stringLengthRange?: StringLengthRange;
  /** Array size range permitted. Relevant for fields that hold arrays, such as those of type `ARRAY`, `ARRAY_STRING`, or `ARRAY_DOCUMENT`. */
  arraySizeRange?: ArraySizeRange;
}

export interface TextValidation {
  /** Minimum permitted length for a text field. */
  minLength?: number | null;
  /** Maximum permitted length for a text field. */
  maxLength?: number | null;
  /** Custom regex pattern for string validation. */
  pattern?: string | null;
}

export interface EnumValues {
  /**
   * Allowed enum values for string fields.
   * @minSize 1
   * @maxSize 512
   * @maxLength 1000
   */
  values?: string[];
}

export enum Type {
  /** Plain-text string. */
  TEXT = 'TEXT',
  /** Number. */
  NUMBER = 'NUMBER',
  /** Date string in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format: `YYYY-MM-DD`. */
  DATE = 'DATE',
  /** [JavaScript Date object](https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Date) ([SDK](https://dev.wix.com/docs/sdk/backend-modules/data/data-types-in-wix-data)) or an object in the following format: `"someFieldKey": { "$date": "YYYY-MM-DDTHH:mm:ss.sssZ"}` ([REST](https://dev.wix.com/docs/rest/business-solutions/cms/data-types-in-wix-data)). */
  DATETIME = 'DATETIME',
  /** Web URL or a [Media Manager](https://dev.wix.com/docs/rest/assets/media/media-manager/introduction) URL. */
  IMAGE = 'IMAGE',
  /** Boolean `true` or `false` value. */
  BOOLEAN = 'BOOLEAN',
  /** Web URL or a [Media Manager](https://dev.wix.com/docs/rest/assets/media/media-manager/introduction) URL. */
  DOCUMENT = 'DOCUMENT',
  /** URL in one of the following formats: `http://`, `https://`, `ftp://`, `mailto:`, `sms:`, `tel:`. */
  URL = 'URL',
  /** [Rich-text](https://dev.wix.com/docs/rest/articles/getting-started/rich-text) string that may contain a subset of HTML tags. */
  RICH_TEXT = 'RICH_TEXT',
  /** Web URL or a [Media Manager](https://dev.wix.com/docs/rest/assets/media/media-manager/introduction) URL. */
  VIDEO = 'VIDEO',
  /** Any field type. */
  ANY = 'ANY',
  /** Array of strings. */
  ARRAY_STRING = 'ARRAY_STRING',
  /** Array of documents: web or [Media Manager](https://dev.wix.com/docs/rest/assets/media/media-manager/introduction) URLs. */
  ARRAY_DOCUMENT = 'ARRAY_DOCUMENT',
  /** Web URL or a [Media Manager](https://dev.wix.com/docs/rest/assets/media/media-manager/introduction) URL. */
  AUDIO = 'AUDIO',
  /** Time string in `hh:mm:ss.SSS` format. */
  TIME = 'TIME',
  /** Language codes in [ISO 639-1](https://en.wikipedia.org/wiki/List_of_ISO_639_language_codes) format. These may include country codes in [ISO 3166-1 alpha-2](https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2#officially_assigned_code_elements) format. */
  LANGUAGE = 'LANGUAGE',
  /** [Rich content](https://dev.wix.com/docs/ricos/getting-started/introduction). */
  RICH_CONTENT = 'RICH_CONTENT',
  /** Array of web or [Media Manager](https://dev.wix.com/docs/rest/assets/media/media-manager/introduction) URLs. */
  MEDIA_GALLERY = 'MEDIA_GALLERY',
  /** Physical address object ([SDK](https://dev.wix.com/docs/sdk/backend-modules/data/data-types-in-wix-data#address-object) | [REST](https://dev.wix.com/docs/rest/business-solutions/cms/data-types-in-wix-data#address-object)). */
  ADDRESS = 'ADDRESS',
  /** [Dynamic page link](https://support.wix.com/en/article/cms-linking-to-dynamic-pages). */
  PAGE_LINK = 'PAGE_LINK',
  /**
   * Reference to another item in another collection. See Query Referenced Data Items ([SDK](https://dev.wix.com/docs/sdk/backend-modules/data/items/query-referenced) | [REST](https://dev.wix.com/docs/rest/business-solutions/cms/data-items/query-referenced-data-items)) to learn about item references.
   *
   * If you select this type, then you must pass the `referencedCollectionId` field as well.
   */
  REFERENCE = 'REFERENCE',
  /** References to multiple items in another collection. See Query Referenced Data Items ([SDK](https://dev.wix.com/docs/sdk/backend-modules/data/items/query-referenced) | [REST](https://dev.wix.com/docs/rest/business-solutions/cms/data-items/query-referenced-data-items)) to learn about item references. */
  MULTI_REFERENCE = 'MULTI_REFERENCE',
  /** JSON object. */
  OBJECT = 'OBJECT',
  /** Array. */
  ARRAY = 'ARRAY',
  /** Deprecated type that might appear in legacy collections. */
  LEGACY_TIME = 'LEGACY_TIME',
  /** Deprecated type that might appear in legacy collections. */
  LEGACY_BOOK = 'LEGACY_BOOK',
  /** Deprecated type that might appear in legacy collections. */
  LEGACY_EXTERNAL_URL = 'LEGACY_EXTERNAL_URL',
  /** Deprecated type that might appear in legacy collections. */
  LEGACY_BROKEN_REFERENCE = 'LEGACY_BROKEN_REFERENCE',
  /** Deprecated type that might appear in legacy collections. */
  LEGACY_IMAGE = 'LEGACY_IMAGE',
  /** Deprecated type that might appear in legacy collections. */
  LEGACY_COLOR = 'LEGACY_COLOR',
  /** Deprecated type that might appear in legacy collections. */
  LEGACY_EXTERNAL_VIDEO = 'LEGACY_EXTERNAL_VIDEO',
  /**
   * Email.
   * @documentationMaturity preview
   */
  EMAIL = 'EMAIL',
  /**
   * Secured media object with digital file metadata.
   * @documentationMaturity preview
   * @internal
   */
  SECURED_MEDIA = 'SECURED_MEDIA',
  /**
   * Media image object.
   * @documentationMaturity preview
   * @internal
   */
  MEDIA_IMAGE = 'MEDIA_IMAGE',
  /**
   * Media vector art object.
   * @documentationMaturity preview
   * @internal
   */
  MEDIA_VECTOR_ART = 'MEDIA_VECTOR_ART',
}

/** @enumType */
export type TypeWithLiterals =
  | Type
  | 'TEXT'
  | 'NUMBER'
  | 'DATE'
  | 'DATETIME'
  | 'IMAGE'
  | 'BOOLEAN'
  | 'DOCUMENT'
  | 'URL'
  | 'RICH_TEXT'
  | 'VIDEO'
  | 'ANY'
  | 'ARRAY_STRING'
  | 'ARRAY_DOCUMENT'
  | 'AUDIO'
  | 'TIME'
  | 'LANGUAGE'
  | 'RICH_CONTENT'
  | 'MEDIA_GALLERY'
  | 'ADDRESS'
  | 'PAGE_LINK'
  | 'REFERENCE'
  | 'MULTI_REFERENCE'
  | 'OBJECT'
  | 'ARRAY'
  | 'LEGACY_TIME'
  | 'LEGACY_BOOK'
  | 'LEGACY_EXTERNAL_URL'
  | 'LEGACY_BROKEN_REFERENCE'
  | 'LEGACY_IMAGE'
  | 'LEGACY_COLOR'
  | 'LEGACY_EXTERNAL_VIDEO'
  | 'EMAIL';

export interface TypeMetadata extends TypeMetadataMetadataOneOf {
  /** Metadata for a reference field. */
  reference?: Reference;
  /** Metadata for a multi-reference field. */
  multiReference?: MultiReference;
  /** Metadata for an object field. */
  object?: _Object;
  /** Metadata for an array field. */
  array?: _Array;
  /** Metadata for a page link field. */
  pageLink?: PageLink;
}

/** @oneof */
export interface TypeMetadataMetadataOneOf {
  /** Metadata for a reference field. */
  reference?: Reference;
  /** Metadata for a multi-reference field. */
  multiReference?: MultiReference;
  /** Metadata for an object field. */
  object?: _Object;
  /** Metadata for an array field. */
  array?: _Array;
  /** Metadata for a page link field. */
  pageLink?: PageLink;
}

export interface FieldCapabilities {
  /**
   * Whether the collection can be sorted by this field.
   *
   * Default: `false`
   */
  sortable?: boolean;
  /**
   * Query operators that can be used for this field.
   * @maxSize 100
   */
  queryOperators?: QueryOperatorWithLiterals[];
}

export enum QueryOperator {
  EQ = 'EQ',
  LT = 'LT',
  GT = 'GT',
  NE = 'NE',
  LTE = 'LTE',
  GTE = 'GTE',
  STARTS_WITH = 'STARTS_WITH',
  ENDS_WITH = 'ENDS_WITH',
  CONTAINS = 'CONTAINS',
  HAS_SOME = 'HAS_SOME',
  HAS_ALL = 'HAS_ALL',
  EXISTS = 'EXISTS',
  URLIZED = 'URLIZED',
}

/** @enumType */
export type QueryOperatorWithLiterals =
  | QueryOperator
  | 'EQ'
  | 'LT'
  | 'GT'
  | 'NE'
  | 'LTE'
  | 'GTE'
  | 'STARTS_WITH'
  | 'ENDS_WITH'
  | 'CONTAINS'
  | 'HAS_SOME'
  | 'HAS_ALL'
  | 'EXISTS'
  | 'URLIZED';

export interface ObjectField {
  /**
   * Field ID.
   * @maxLength 1000
   */
  key?: string;
  /**
   * Display name for the field.
   * @maxLength 1000
   */
  displayName?: string | null;
  /**
   * Field type.
   *
   * Learn more about [data types in Wix Data](https://dev.wix.com/docs/rest/business-solutions/cms/data-types-in-wix-data).
   */
  type?: TypeWithLiterals;
  /** Metadata for complex data types. This property only exists for references, multi-references, objects, and arrays. */
  typeMetadata?: TypeMetadata;
  /**
   * Capabilities the object field supports.
   * @readonly
   */
  capabilities?: FieldCapabilities;
}

export interface FieldsPattern {
  /** @maxLength 1000 */
  pattern?: string;
  lowercase?: boolean;
}

export interface UrlizedOnlyPattern {
  /** @maxLength 1000 */
  pattern?: string;
}

export interface Calculator extends CalculatorPatternOneOf {
  /** Value is calculated according to pattern, whitespaces are replaced with dash [-]. */
  fieldsPattern?: FieldsPattern;
  /** Value is only URL encoded. */
  urlizedOnlyPattern?: UrlizedOnlyPattern;
}

/** @oneof */
export interface CalculatorPatternOneOf {
  /** Value is calculated according to pattern, whitespaces are replaced with dash [-]. */
  fieldsPattern?: FieldsPattern;
  /** Value is only URL encoded. */
  urlizedOnlyPattern?: UrlizedOnlyPattern;
}

export enum PageLinkType {
  /**
   * Page link with field substitution patterns stored in the data collection.
   * Example: "path/{plainField}" where plainField can be any supported type.
   * Can be persistent (stored in database) or overlay (calculated on-the-fly).
   */
  COLLECTION_DEFINED = 'COLLECTION_DEFINED',
  /**
   * Page link with field substitution patterns provided by Routers Config.
   * Only supports SLUG type fields in the template (e.g., "products/{slug}").
   * Always read-only overlay fields (never persisted in database).
   * Page link definitions are NOT stored in the data collection.
   */
  ROUTER_DEFINED = 'ROUTER_DEFINED',
}

/** @enumType */
export type PageLinkTypeWithLiterals =
  | PageLinkType
  | 'COLLECTION_DEFINED'
  | 'ROUTER_DEFINED';

export interface Reference {
  /**
   * Referenced collection ID.
   * @maxLength 256
   */
  referencedCollectionId?: string;
}

export interface MultiReference {
  /**
   * Referenced collection ID.
   * @maxLength 256
   */
  referencedCollectionId?: string;
  /**
   * Referencing field ID.
   * @maxLength 1000
   */
  referencingFieldKey?: string;
  /**
   * Display name in the CMS for the referenced data.
   * @maxLength 1000
   */
  referencingDisplayName?: string;
}

export interface _Object {
  /**
   * Fields within the object.
   * @maxSize 1000
   */
  fields?: ObjectField[];
}

export interface _Array {
  /** Element's data type. */
  elementType?: TypeWithLiterals;
  /** Metadata for complex data types. This property only exists for references, multi-references, objects, and arrays. */
  typeMetadata?: TypeMetadata;
}

export interface PageLink {
  calculator?: Calculator;
  /**
   * Defines reference to router pattern in the site document.
   * @maxLength 1000
   */
  linkedRouterPage?: string | null;
}

export interface Slug {
  /**
   * @minLength 1
   * @maxLength 1000
   */
  pattern?: string;
}

export interface Text extends TextValidationOneOf {
  textValidation?: TextValidation;
  enumValues?: EnumValues;
  defaultValue?: string | null;
}

/** @oneof */
export interface TextValidationOneOf {
  textValidation?: TextValidation;
  enumValues?: EnumValues;
}

export interface _Number {
  defaultValue?: number | null;
  /** Minimum permitted value for a numerical field. */
  min?: number | null;
  /** Maximum permitted value for a numerical field. */
  max?: number | null;
}

export interface _Date
  extends _DateDefaultValueOneOf,
    _DateMinDateOneOf,
    _DateMaxDateOneOf {
  defaultValueFixed?: Date | null;
  defaultValueOffsetFromNowInDays?: number | null;
  minDateFixed?: Date | null;
  minValueOffsetFromNowInDays?: number | null;
  maxDateFixed?: Date | null;
  maxValueOffsetFromNowInDays?: number | null;
}

/** @oneof */
export interface _DateDefaultValueOneOf {
  defaultValueFixed?: Date | null;
  defaultValueOffsetFromNowInDays?: number | null;
}

/** @oneof */
export interface _DateMinDateOneOf {
  minDateFixed?: Date | null;
  minValueOffsetFromNowInDays?: number | null;
}

/** @oneof */
export interface _DateMaxDateOneOf {
  maxDateFixed?: Date | null;
  maxValueOffsetFromNowInDays?: number | null;
}

export interface Datetime
  extends DatetimeDefaultValueOneOf,
    DatetimeMinDateOneOf,
    DatetimeMaxDateOneOf {
  defaultValueFixed?: Date | null;
  defaultValueOffsetFromNowInSeconds?: number | null;
  minDateFixed?: Date | null;
  minDateOffsetFromNowInSeconds?: number | null;
  maxDateFixed?: Date | null;
  maxDateOffsetFromNowInSeconds?: number | null;
}

/** @oneof */
export interface DatetimeDefaultValueOneOf {
  defaultValueFixed?: Date | null;
  defaultValueOffsetFromNowInSeconds?: number | null;
}

/** @oneof */
export interface DatetimeMinDateOneOf {
  minDateFixed?: Date | null;
  minDateOffsetFromNowInSeconds?: number | null;
}

/** @oneof */
export interface DatetimeMaxDateOneOf {
  maxDateFixed?: Date | null;
  maxDateOffsetFromNowInSeconds?: number | null;
}

export interface Image {
  defaultValue?: string | null;
}

export interface _Boolean {
  defaultValue?: boolean | null;
}

export interface Document {
  defaultValue?: string | null;
}

export interface Url {
  /**
   * @minLength 1
   * @maxLength 2048
   */
  defaultValue?: string | null;
  textValidation?: TextValidation;
}

export interface RichText {
  defaultValue?: string | null;
  /** Minimum permitted length for a string field. */
  minLength?: number | null;
  /** Maximum permitted length for a string field. */
  maxLength?: number | null;
}

export interface Video {
  defaultValue?: string | null;
}

export interface ArrayString {
  defaultValue?: string[] | null;
  /** Minimum permitted number of items in an array field. */
  minSize?: number | null;
  /** Maximum permitted number of items in an array field. */
  maxSize?: number | null;
  /** Minimum permitted length for a each item. */
  minLengthForItem?: number | null;
  /** Maximum permitted length for a each item. */
  maxLengthForItem?: number | null;
}

export interface ArrayDocument {
  defaultValue?: string[] | null;
  /** Minimum permitted number of items in an array field. */
  minSize?: number | null;
  /** Maximum permitted number of items in an array field. */
  maxSize?: number | null;
}

export interface Audio {
  defaultValue?: string | null;
}

export interface Time {
  /**
   * Time value in format: hh:mm (e.g., "00:00", "09:30").
   * @minLength 5
   * @maxLength 5
   */
  defaultValue?: string | null;
  /**
   * Minimum permitted time-of-day (inclusive).
   * Time value in format: hh:mm (e.g., "00:00", "09:30").
   * @minLength 5
   * @maxLength 5
   */
  minTime?: string | null;
  /**
   * Maximum permitted time-of-day (inclusive).
   * Time value in format: hh:mm (e.g., "23:59", "17:00").
   * @minLength 5
   * @maxLength 5
   */
  maxTime?: string | null;
}

export interface RichContent {
  defaultValue?: Record<string, any> | null;
}

export interface MediaGallery {
  defaultValue?: Record<string, any>[] | null;
}

export interface Address {
  defaultValue?: Record<string, any> | null;
}

export interface Email {
  /** @format EMAIL */
  defaultValue?: string | null;
}

export interface LegacyColor {
  defaultValue?: string | null;
}

export interface NumberRange {
  /**
   * Minimum permitted value for a numerical field.
   *
   * Default: No validation
   */
  min?: number | null;
  /**
   * Maximum permitted value for a numerical field.
   *
   * Default: No validation
   */
  max?: number | null;
}

export interface StringLengthRange {
  /**
   * Minimum permitted length for a text field.
   *
   * Default: No validation
   */
  minLength?: number | null;
  /**
   * Maximum permitted length for a text field.
   *
   * Default: No validation
   */
  maxLength?: number | null;
}

export interface ArraySizeRange {
  /**
   * Minimum permitted number of items in an array field. Relevant for fields that hold arrays, such as those of type `ARRAY`, `ARRAY_STRING`, or `ARRAY_DOCUMENT`.
   *
   * Default: No validation
   */
  minSize?: number | null;
  /**
   * Maximum permitted number of items in an array field. Relevant for fields that hold arrays, such as those of type `ARRAY`, `ARRAY_STRING`, or `ARRAY_DOCUMENT`.
   *
   * Default: No validation
   */
  maxSize?: number | null;
}

/** Optional plug-in aspects for fields */
export interface FieldPlugin extends FieldPluginOptionsOneOf {
  /** Options for the CMS plugin. */
  cmsOptions?: CmsOptions;
  /** Options for the CATEGORY_FIELD plugin. */
  categoryFieldOptions?: CategoryFieldOptions;
  type?: FieldPluginTypeWithLiterals;
}

/** @oneof */
export interface FieldPluginOptionsOneOf {
  /** Options for the CMS plugin. */
  cmsOptions?: CmsOptions;
  /** Options for the CATEGORY_FIELD plugin. */
  categoryFieldOptions?: CategoryFieldOptions;
}

export enum FieldPluginType {
  /** Uknown plugin type. */
  UNKNOWN = 'UNKNOWN',
  /** CMS-related field attributes */
  CMS = 'CMS',
}

/** @enumType */
export type FieldPluginTypeWithLiterals = FieldPluginType | 'UNKNOWN' | 'CMS';

/** Options for the CMS plugin. */
export interface CmsOptions {
  /**
   * Indicates an internal CMS field. The CMS does not display internal fields.
   *
   * Default: `false`
   */
  internal?: boolean;
}

/** Options for the CATEGORY_FIELD plugin. */
export interface CategoryFieldOptions {
  /**
   * Transient input-only flag. When set to `true` during create/update, designates
   * this category field's group as the target for `_primaryCategory`. The flag is
   * not persisted; `_primaryCategory` remains the source of truth.
   *
   * Default: `false`
   */
  isPrimary?: boolean;
}

export interface FieldCondition {
  /**
   * If `group_name` is set, this field will not be present in query results. To retrieve this field, pass `group_name` value to `includeFieldGroups` parameter in query operations.
   * @maxLength 100
   */
  groupName?: string | null;
  /** If present, the field may be omitted from the results, if the requester lacks sufficient permissions to access data stored in the field. */
  permission?: PermissionWithLiterals;
}

export enum Permission {
  /** Any role can request the field. */
  ANYONE = 'ANYONE',
  /** Special permission required to request the field. */
  RESTRICTED = 'RESTRICTED',
}

/** @enumType */
export type PermissionWithLiterals = Permission | 'ANYONE' | 'RESTRICTED';

/** Permissions defined by the lowest role needed to perform each action. */
export interface Permissions {
  /**
   * Role required to insert items into the collection.
   *
   * Default: `CMS_EDITOR`
   */
  insert?: RoleWithLiterals;
  /**
   * Role required to update collection items.
   *
   * Default: `CMS_EDITOR`
   */
  update?: RoleWithLiterals;
  /**
   * Role required to remove items from the collection.
   *
   * Default: `CMS_EDITOR`
   */
  remove?: RoleWithLiterals;
  /**
   * Role required to read collection items.
   *
   * Default: `SITE_MEMBER_AUTHOR`
   */
  read?: RoleWithLiterals;
}

export enum Role {
  /** Site administrator. */
  ADMIN = 'ADMIN',
  /** Signed-in user who added content to this collection. */
  SITE_MEMBER_AUTHOR = 'SITE_MEMBER_AUTHOR',
  /** Any signed-in user. */
  SITE_MEMBER = 'SITE_MEMBER',
  /** Any site visitor. */
  ANYONE = 'ANYONE',
}

/** @enumType */
export type RoleWithLiterals =
  | Role
  | 'ADMIN'
  | 'SITE_MEMBER_AUTHOR'
  | 'SITE_MEMBER'
  | 'ANYONE';

/** Plugins that extend collection functionality. */
export interface Plugin extends PluginOptionsOneOf {
  /**
   * Options for the Publish plugin.
   * This plugin allows items in a [data collection](https://dev.wix.com/docs/rest/business-solutions/cms/data-collections/data-collection-object) to be marked as draft or published. Published items are visible to site visitors, while draft items are not.
   */
  publishOptions?: PublishPluginOptions;
  /** Options for the Single Item plugin. */
  singleItemOptions?: SingleItemPluginOptions;
  /** Options for the Urlized plugin. */
  urlizedOptions?: UrlizedPluginOptions;
  /** Options for the Multilingual plugin. */
  multilingualOptions?: MultilingualOptions;
  /** Options for the PageLink plugin. */
  editablePageLinkOptions?: PageLinkPluginOptions;
  /** Options for the CMS plugin. */
  cmsOptions?: PluginCmsOptions;
  /** Plugin type. */
  type?: PluginTypeWithLiterals;
}

/** @oneof */
export interface PluginOptionsOneOf {
  /**
   * Options for the Publish plugin.
   * This plugin allows items in a [data collection](https://dev.wix.com/docs/rest/business-solutions/cms/data-collections/data-collection-object) to be marked as draft or published. Published items are visible to site visitors, while draft items are not.
   */
  publishOptions?: PublishPluginOptions;
  /** Options for the Single Item plugin. */
  singleItemOptions?: SingleItemPluginOptions;
  /** Options for the Urlized plugin. */
  urlizedOptions?: UrlizedPluginOptions;
  /** Options for the Multilingual plugin. */
  multilingualOptions?: MultilingualOptions;
  /** Options for the PageLink plugin. */
  editablePageLinkOptions?: PageLinkPluginOptions;
  /** Options for the CMS plugin. */
  cmsOptions?: PluginCmsOptions;
}

export enum Status {
  PUBLISHED = 'PUBLISHED',
  DRAFT = 'DRAFT',
}

/** @enumType */
export type StatusWithLiterals = Status | 'PUBLISHED' | 'DRAFT';

export enum LifecycleStatus {
  ACTIVE = 'ACTIVE',
  MIGRATING_TO_DRAFT_ITEMS = 'MIGRATING_TO_DRAFT_ITEMS',
}

/** @enumType */
export type LifecycleStatusWithLiterals =
  | LifecycleStatus
  | 'ACTIVE'
  | 'MIGRATING_TO_DRAFT_ITEMS';

export enum Format {
  /** Letters are converted to lower case and spaces are replaced with dashes before generating the encoded URL. */
  ORIGINAL = 'ORIGINAL',
  /** No changes are made before generating the encoded URL. */
  PLAIN = 'PLAIN',
}

/** @enumType */
export type FormatWithLiterals = Format | 'ORIGINAL' | 'PLAIN';

/** if CMS-defined sort is enabled and should be used in site */
export interface SiteSort {
  /**
   * Field and order for the site sort.
   * @maxSize 100
   */
  sort?: Sort[];
}

/**
 * Describes who can perform certain actions.
 * Each level includes all levels below it (except UNDEFINED).
 */
export enum AccessLevel {
  /** Any subject, including visitors. */
  ANYONE = 'ANYONE',
  /** Any signed-in user (both site members and collaborators). */
  SITE_MEMBER = 'SITE_MEMBER',
  /** Any signed-in user, but site members only have access to own items. */
  SITE_MEMBER_AUTHOR = 'SITE_MEMBER_AUTHOR',
  /** Site collaborator that has a role with CMS Access permission. */
  CMS_EDITOR = 'CMS_EDITOR',
  /** CMS administrators and users or roles granted with special access. */
  PRIVILEGED = 'PRIVILEGED',
}

/** @enumType */
export type AccessLevelWithLiterals =
  | AccessLevel
  | 'ANYONE'
  | 'SITE_MEMBER'
  | 'SITE_MEMBER_AUTHOR'
  | 'CMS_EDITOR'
  | 'PRIVILEGED';

export interface DraftPermissions {}

export interface AllowedDraftPermissions {
  /** If manage_drafts is permitted for the caller. */
  manageDrafts?: boolean;
  /** If manage_publish_status is permitted for the caller. */
  managePublishStatus?: boolean;
}

export enum PluginType {
  /** Allows items to be marked as either draft or published. For each item you can set a publishing time when the item will become visible to site visitors. */
  PUBLISH = 'PUBLISH',
  /** Ensures the collection can have one item at most. Can only be applied to a new collection. */
  SINGLE_ITEM = 'SINGLE_ITEM',
  /** Generates item URLs for collections used by dynamic pages. */
  URLIZED = 'URLIZED',
  /** Deprecated. Will be removed in the future. */
  GRIDAPPLESS = 'GRIDAPPLESS',
  /** Indicates that the collection is translatable. This allows you to manage translation for selected fields using [Wix Multilingual](https://www.wix.com/app-market/wix-multilingual). */
  MULTILINGUAL = 'MULTILINGUAL',
  /** Indicates that collection is shared with current site. */
  SHARED = 'SHARED',
  /** Indicates that page link fields are persisted and can be updated. */
  EDITABLE_PAGE_LINK = 'EDITABLE_PAGE_LINK',
  /** CMS-specific collection properties. */
  CMS = 'CMS',
}

/** @enumType */
export type PluginTypeWithLiterals =
  | PluginType
  | 'PUBLISH'
  | 'SINGLE_ITEM'
  | 'URLIZED'
  | 'GRIDAPPLESS'
  | 'MULTILINGUAL'
  | 'SHARED'
  | 'EDITABLE_PAGE_LINK'
  | 'CMS';

export interface PublishPluginOptions {
  /** Default status. */
  defaultStatus?: StatusWithLiterals;
  /**
   * Server-managed lifecycle status.
   * @internal
   * @readonly
   */
  lifecycleStatus?: LifecycleStatusWithLiterals;
}

export interface SingleItemPluginOptions {
  /**
   * ID of the single item in this collection. If you insert or update an item, its ID value is always changed to this.
   * @maxLength 16000
   */
  singleItemId?: string;
}

export interface UrlizedPluginOptions {
  /** Encoding method for generating a URL in ASCII characters. */
  format?: FormatWithLiterals;
}

export interface MultilingualOptions {
  /**
   * IDs of fields to allow translation.
   * @maxSize 1000
   * @maxLength 1000
   */
  translatableFieldKeys?: string[];
}

export interface PageLinkPluginOptions {
  isPersisted?: boolean;
  isMutable?: boolean;
}

export interface PluginCmsOptions {
  /** CMS sort, applied when a collection is displayed on a site. */
  siteSort?: SiteSort;
}

export interface CatalogOptions {
  /**
   * *Required** - the field key that contains the catalog item name - field must be of type TEXT.
   * @maxLength 1000
   * @deprecated
   * @replacedBy name_field_key
   * @targetRemovalDate 2026-07-01
   */
  name?: string;
  /**
   * *Required** - the field key that contains the catalog item price - field must be of type NUMBER.
   * @maxLength 1000
   * @deprecated
   * @replacedBy price_field_key
   * @targetRemovalDate 2026-07-01
   */
  price?: string;
  /**
   * the field key that contains the catalog item URL - field must be of type URL.
   * @maxLength 1000
   * @deprecated
   * @replacedBy url_field_key
   * @targetRemovalDate 2026-07-01
   */
  url?: string | null;
  /**
   * the field key that contains the catalog item description - field must be of type TEXT.
   * @maxLength 1000
   * @deprecated
   * @replacedBy description_field_key
   * @targetRemovalDate 2026-07-01
   */
  description?: string | null;
  /**
   * the field key that contains the catalog item image - field must be of type IMAGE.
   * @maxLength 1000
   * @deprecated
   * @replacedBy image_field_key
   * @targetRemovalDate 2026-07-01
   */
  image?: string | null;
  /**
   * the field key that contains the catalog item quantity - field must be of type NUMBER.
   * @maxLength 1000
   * @deprecated
   * @replacedBy quantity_field_key
   * @targetRemovalDate 2026-07-01
   */
  quantity?: string | null;
  /**
   * the field key that contains the catalog item digital file - field must be of type SECURED_MEDIA.
   * @maxLength 1000
   * @deprecated
   * @replacedBy digital_file_field_key
   * @targetRemovalDate 2026-07-01
   */
  digitalFile?: string | null;
  /**
   * the field key that contains the catalog item weight - field must be of type NUMBER.
   * @maxLength 1000
   * @deprecated
   * @replacedBy weight_field_key
   * @targetRemovalDate 2026-07-01
   */
  weight?: string | null;
  /**
   * the field key that contains the catalog item SKU - field must be of type TEXT.
   * @maxLength 1000
   * @deprecated
   * @replacedBy sku_field_key
   * @targetRemovalDate 2026-07-01
   */
  sku?: string | null;
  /**
   * the field key that contains the catalog item shippable flag - field must be of type BOOLEAN.
   * @maxLength 1000
   * @deprecated
   * @replacedBy shippable_field_key
   * @targetRemovalDate 2026-07-01
   */
  shippable?: string | null;
  /**
   * *Required** - the field key that contains the catalog item name - field must be of type TEXT.
   * @maxLength 1000
   */
  nameFieldKey?: string;
  /**
   * *Required** - the field key that contains the catalog item price - field must be of type NUMBER.
   * @maxLength 1000
   */
  priceFieldKey?: string;
  /**
   * the field key that contains the catalog item URL - field must be of type URL.
   * @maxLength 1000
   */
  urlFieldKey?: string | null;
  /**
   * the field key that contains the catalog item description - field must be of type TEXT.
   * @maxLength 1000
   */
  descriptionFieldKey?: string | null;
  /**
   * the field key that contains the catalog item image - field must be of type IMAGE.
   * @maxLength 1000
   */
  imageFieldKey?: string | null;
  /**
   * the field key that contains the catalog item quantity - field must be of type NUMBER.
   * @maxLength 1000
   */
  quantityFieldKey?: string | null;
  /**
   * the field key that contains the catalog item digital file - field must be of type SECURED_MEDIA.
   * @maxLength 1000
   */
  digitalFileFieldKey?: string | null;
  /**
   * the field key that contains the catalog item weight - field must be of type NUMBER.
   * @maxLength 1000
   */
  weightFieldKey?: string | null;
  /**
   * the field key that contains the catalog item SKU - field must be of type TEXT.
   * @maxLength 1000
   */
  skuFieldKey?: string | null;
  /**
   * the field key that contains the catalog item shippable flag - field must be of type BOOLEAN.
   * @maxLength 1000
   */
  shippableFieldKey?: string | null;
}

export interface DraftItemsPluginOptions
  extends DraftItemsPluginOptionsPairedCollectionOneOf {
  /**
   * ID of the paired drafts collection (e.g. "Posts__drafts").
   * Present only when this plugin belongs to the published collection.
   * @readonly
   */
  draftsCollectionId?: string;
  /**
   * ID of the paired published collection (e.g. "Posts").
   * Present only when this plugin belongs to the drafts collection.
   * @readonly
   */
  publishedCollectionId?: string;
}

/** @oneof */
export interface DraftItemsPluginOptionsPairedCollectionOneOf {
  /**
   * ID of the paired drafts collection (e.g. "Posts__drafts").
   * Present only when this plugin belongs to the published collection.
   * @readonly
   */
  draftsCollectionId?: string;
  /**
   * ID of the paired published collection (e.g. "Posts").
   * Present only when this plugin belongs to the drafts collection.
   * @readonly
   */
  publishedCollectionId?: string;
}

export enum PagingMode {
  /** Offset-based paging. */
  OFFSET = 'OFFSET',
  /** Cursor-based paging. */
  CURSOR = 'CURSOR',
}

/** @enumType */
export type PagingModeWithLiterals = PagingMode | 'OFFSET' | 'CURSOR';

/** Permissions defined by access level for each action. */
export interface DataPermissions {
  /**
   * Access level required to read collection items.
   *
   * Default: `SITE_MEMBER_AUTHOR`
   */
  itemRead?: AccessLevelWithLiterals;
  /**
   * Access level required to insert items into the collection.
   *
   * Default: `CMS_EDITOR`
   */
  itemInsert?: AccessLevelWithLiterals;
  /**
   * Access level required to update collection items.
   *
   * Default: `CMS_EDITOR`
   */
  itemUpdate?: AccessLevelWithLiterals;
  /**
   * Access level required to remove items from the collection.
   *
   * Default: `CMS_EDITOR`
   */
  itemRemove?: AccessLevelWithLiterals;
}

export interface AllowedDataPermissions {
  /** If data items read permitted */
  itemRead?: boolean;
  /** If for data items insert permitted */
  itemInsert?: boolean;
  /** If data items update permitted */
  itemUpdate?: boolean;
  /** If data items removal permitted */
  itemRemove?: boolean;
}

export interface DataCollectionClonedEvent {
  /**
   * Instance ID of the collection from which the data is cloned.
   * @format GUID
   */
  originInstanceId?: string;
  /**
   * ID of the collection from which the data is cloned.
   * @maxLength 256
   */
  originId?: string;
}

export interface DataCollectionChangedEvent {
  /**
   * list of new fields
   * @maxSize 1000
   */
  fieldsAdded?: Field[];
  /**
   * list of changed fields
   * @maxSize 1000
   */
  fieldsUpdated?: FieldUpdate[];
  /**
   * list of removed fields
   * @maxSize 1000
   */
  fieldsRemoved?: Field[];
  /**
   * list of new plugins
   * @maxSize 100
   */
  pluginsAdded?: Plugin[];
  /**
   * list of changed plugins
   * @maxSize 100
   */
  pluginsUpdated?: PluginUpdate[];
  /**
   * list of removed plugins
   * @maxSize 100
   */
  pluginsRemoved?: Plugin[];
  /** Permission change details. Only present if permissions were actually changed. */
  permissionsUpdated?: PermissionsUpdate;
  /** Strict mode change details. Only present if strict mode was actually changed. */
  strictModeUpdated?: StrictModeUpdate;
}

export interface FieldUpdate {
  /** previous state of the field */
  previous?: Field;
  /** current state of the field */
  current?: Field;
}

export interface PluginUpdate {
  /** previous state of the plugin */
  previous?: Plugin;
  /** current state of the plugin */
  current?: Plugin;
}

export interface PermissionsUpdate {
  /** Previous permissions state. */
  previous?: DataPermissions;
  /** Current permissions state. */
  current?: DataPermissions;
}

export interface StrictModeUpdate {
  /** Previous strict mode value. */
  previous?: boolean | null;
  /** Current strict mode value. */
  current?: boolean | null;
}

export interface DataCollectionEvictedEvent {
  /**
   * ID of the collection that was permanently deleted.
   * @minLength 1
   * @maxLength 1000
   */
  _id?: string;
}

export interface CreateDataCollectionRequest {
  /** Collection details. */
  collection: DataCollection;
}

export interface CreateDataCollectionResponse {
  /** Details of collection created. */
  collection?: DataCollection;
}

export interface GetDataCollectionRequest {
  /**
   * ID of the collection to retrieve.
   * @maxLength 256
   */
  dataCollectionId: string;
  /**
   * Whether to retrieve data from the primary database instance.
   * This decreases performance but ensures data retrieved is up to date even immediately after an update.
   * Learn more about [Wix Data and eventual consistency](https://dev.wix.com/api/rest/wix-data/wix-data/eventual-consistency).
   *
   * Default: `false`
   */
  consistentRead?: boolean;
  /**
   * If set allowed for caller data permissions would be included in the response
   * @internal
   */
  includeAllowedDataPermissions?: boolean;
  /**
   * List of specific field names to return, if empty all fields are returned.
   * Affects all returned collections
   * @maxSize 100
   * @maxLength 1000
   */
  fields?: string[];
}

export enum Segment {
  UNKNOWN_SEGMENT = 'UNKNOWN_SEGMENT',
  PUBLIC = 'PUBLIC',
  DEV = 'DEV',
}

/** @enumType */
export type SegmentWithLiterals =
  | Segment
  | 'UNKNOWN_SEGMENT'
  | 'PUBLIC'
  | 'DEV';

export interface GetDataCollectionResponse {
  /** Details of the collection requested. */
  collection?: DataCollection;
}

export interface ListDataCollectionsRequest {
  /**
   * Defines how collections in the response are sorted.
   *
   * Default: Ordered by ID in ascending order.
   */
  sort?: Sorting;
  /** Pagination information. */
  paging?: Paging;
  /**
   * Whether to retrieve data from the primary database instance.
   * This decreases performance but ensures data retrieved is up to date even immediately after an update.
   * Learn more about [Wix Data and eventual consistency](https://dev.wix.com/api/rest/wix-data/wix-data/eventual-consistency).
   *
   * Default: `false`
   */
  consistentRead?: boolean;
  /**
   * If set allowed for caller data permissions would be included in the response
   * @internal
   */
  includeAllowedDataPermissions?: boolean;
  /**
   * List of specific field names to return, if empty all fields are returned.
   * Affects all returned collections
   * @maxSize 100
   * @maxLength 128
   */
  fields?: string[];
}

export interface Sorting {
  /**
   * Name of the field to sort by.
   * @maxLength 1000
   */
  fieldName?: string;
  /** Sort order. */
  order?: SortOrderWithLiterals;
}

export enum SortOrder {
  ASC = 'ASC',
  DESC = 'DESC',
}

/** @enumType */
export type SortOrderWithLiterals = SortOrder | 'ASC' | 'DESC';

export interface Paging {
  /** Number of items to load. */
  limit?: number | null;
  /** Number of items to skip in the current sort order. */
  offset?: number | null;
}

export interface ListDataCollectionsResponse {
  /** List of collections. */
  collections?: DataCollection[];
  /** Paging information. */
  pagingMetadata?: PagingMetadataV2;
}

export interface PagingMetadataV2 {
  /** Number of items returned in the response. */
  count?: number | null;
  /** Offset that was requested. */
  offset?: number | null;
  /** Total number of items that match the query. Returned if offset paging is used and the `tooManyToCount` flag is not set. */
  total?: number | null;
  /** Flag that indicates the server failed to calculate the `total` field. */
  tooManyToCount?: boolean | null;
}

export interface BulkGetDataCollectionsRequest {
  /**
   * IDs of the collections to retrieve.
   * @minSize 1
   * @maxSize 100
   * @maxLength 256
   */
  dataCollectionIds?: string[];
  /**
   * Whether to include deleted collections.
   *
   * Default: `false`
   */
  showDeletedCollections?: boolean;
  /**
   * Whether the returned collection list should include referenced collections.
   *
   * Default: `false`
   */
  includeReferencedCollections?: boolean;
  /** Sorting preferences. */
  sort?: Sorting;
  /**
   * Whether to retrieve data from the primary database instance.
   * This decreases performance but ensures data retrieved is up to date even immediately after an update.
   * Learn more about [Wix Data and eventual consistency](https://dev.wix.com/api/rest/wix-data/wix-data/eventual-consistency).
   *
   * Default: `false`
   */
  consistentRead?: boolean;
  /**
   * If set allowed for caller data permissions would be included in the response
   * @internal
   */
  includeAllowedDataPermissions?: boolean;
  /**
   * List of specific field names to return, if empty all fields are returned.
   * Affects all returned collections
   * @maxSize 100
   * @maxLength 1000
   */
  fields?: string[];
}

export interface BulkGetDataCollectionsResponse {
  /**
   * List of requested collections.
   * When `include_referenced_collections` is `true` in the request, referenced collections are included here.
   */
  activeCollections?: DataCollection[];
  /** List of requested deleted collections. Only populated when `showDeletedCollections` is true in the request. */
  deletedCollections?: DataCollection[];
  /** List of failed collections. */
  failedCollections?: FailedCollection[];
}

export interface ApplicationError {
  /** Error code. */
  code?: string;
  /** Description of the error. */
  description?: string;
  /** Data related to the error. */
  data?: Record<string, any> | null;
}

export interface FailedCollection {
  /**
   * IDs of the failed collection.
   * @maxLength 255
   */
  collectionId?: string;
  /** Reason of the failure. */
  error?: ApplicationError;
}

export interface UpdateDataCollectionRequest {
  /** Updated collection details. The existing collection is replaced with this version. */
  collection: DataCollection;
}

export interface UpdateDataCollectionResponse {
  /** Updated collection details. */
  collection?: DataCollection;
}

export interface DeleteDataCollectionRequest {
  /**
   * ID of the collection to delete.
   * @maxLength 256
   */
  dataCollectionId: string;
}

export interface DeleteDataCollectionResponse {}

export interface RestoreDataCollectionRequest {
  /**
   * Data Collection ID to restore
   * @maxLength 256
   */
  dataCollectionId?: string;
}

export interface RestoreDataCollectionResponse {
  /** Restored data collection */
  dataCollection?: DataCollection;
}

export interface PatchDataCollectionRequest {
  /** Data collection to update. */
  dataCollection: DataCollection;
}

export interface PatchDataCollectionResponse {
  /** Updated data collection. */
  dataCollection?: DataCollection;
}

export interface CreateDataCollectionFieldRequest {
  /**
   * ID of the data collection to update.
   * @maxLength 256
   */
  dataCollectionId: string;
  /** New field to add. */
  field: Field;
}

export interface CreateDataCollectionFieldResponse {
  /** Updated data collection. */
  dataCollection?: DataCollection;
}

export interface UpdateDataCollectionFieldRequest {
  /**
   * ID of the data collection to update.
   * @maxLength 256
   */
  dataCollectionId: string;
  /** Field to update. */
  field: Field;
}

export interface UpdateDataCollectionFieldResponse {
  /** Updated data collection. */
  dataCollection?: DataCollection;
}

export interface DeleteDataCollectionFieldRequest {
  /**
   * ID of the data collection to update.
   * @maxLength 256
   */
  dataCollectionId: string;
  /**
   * ID of the field to delete.
   * @maxLength 1000
   */
  fieldKey: string;
}

export interface DeleteDataCollectionFieldResponse {
  /** Updated data collection. */
  dataCollection?: DataCollection;
}

export interface AddDataCollectionPluginRequest {
  /**
   * ID of the data collection to update.
   * @maxLength 256
   */
  dataCollectionId: string;
  /**
   * Plugin to add to the collection. Must include the plugin type and any type-specific configuration.
   * If a plugin of the same type already exists in the collection, this operation fails.
   */
  plugin: Plugin;
}

export interface AddDataCollectionPluginResponse {
  /** Updated data collection. */
  dataCollection?: DataCollection;
}

export interface DeleteDataCollectionPluginRequest {
  /**
   * ID of the data collection to update.
   * @maxLength 256
   */
  dataCollectionId: string;
  /** Plugin type to remove. */
  pluginType: PluginTypeWithLiterals;
}

export interface DeleteDataCollectionPluginResponse {
  /** Updated data collection. */
  dataCollection?: DataCollection;
}

export interface AddPublishPluginRequest {
  /**
   * ID of the data collection to update.
   * @maxLength 256
   */
  dataCollectionId: string;
  /** Publish plugin options. */
  publishOptions: PublishPluginOptions;
  /** Action to take when the Draft Items plugin is already installed. */
  draftItemsPluginConflictAction?: DraftItemsPluginConflictActionWithLiterals;
}

/** Behavior to apply when adding the Publish plugin to a collection that already has the Draft Items plugin. */
export enum DraftItemsPluginConflictAction {
  /** Fail if the Draft Items plugin is already installed. */
  FAIL_WHEN_DRAFT_ITEMS_INSTALLED = 'FAIL_WHEN_DRAFT_ITEMS_INSTALLED',
  /** Discard all draft items in the Draft Items plugin, causing data loss. */
  DISCARD_DRAFT_ITEMS = 'DISCARD_DRAFT_ITEMS',
  /** Publish all draft items. This overwrites any currently published items with the same IDs. */
  PUBLISH_DRAFT_ITEMS = 'PUBLISH_DRAFT_ITEMS',
  /** Copy draft items with draft status. If a published version already exists, discard the draft item. */
  COPY_DRAFT_ITEMS_WITH_DRAFT_STATUS = 'COPY_DRAFT_ITEMS_WITH_DRAFT_STATUS',
}

/** @enumType */
export type DraftItemsPluginConflictActionWithLiterals =
  | DraftItemsPluginConflictAction
  | 'FAIL_WHEN_DRAFT_ITEMS_INSTALLED'
  | 'DISCARD_DRAFT_ITEMS'
  | 'PUBLISH_DRAFT_ITEMS'
  | 'COPY_DRAFT_ITEMS_WITH_DRAFT_STATUS';

export interface AddPublishPluginResponse {
  /** Updated data collection. */
  dataCollection?: DataCollection;
}

export interface DeletePublishPluginRequest {
  /**
   * ID of the data collection to update.
   * @maxLength 256
   */
  dataCollectionId: string;
}

export interface DeletePublishPluginResponse {
  /** Updated data collection. */
  dataCollection?: DataCollection;
}

export interface AddUrlizedPluginRequest {
  /**
   * ID of the data collection to update.
   * @maxLength 256
   */
  dataCollectionId: string;
  /** Urlized plugin options. */
  urlizedOptions: UrlizedPluginOptions;
}

export interface AddUrlizedPluginResponse {
  /** Updated data collection. */
  dataCollection?: DataCollection;
}

export interface DeleteUrlizedPluginRequest {
  /**
   * ID of the data collection to update.
   * @maxLength 256
   */
  dataCollectionId: string;
}

export interface DeleteUrlizedPluginResponse {
  /** Updated data collection. */
  dataCollection?: DataCollection;
}

export interface AddMultilingualPluginRequest {
  /**
   * ID of the data collection to update.
   * @maxLength 256
   */
  dataCollectionId: string;
  /** Multilingual plugin options. */
  multilingualOptions: MultilingualOptions;
}

export interface AddMultilingualPluginResponse {
  /** Updated data collection. */
  dataCollection?: DataCollection;
}

export interface DeleteMultilingualPluginRequest {
  /**
   * ID of the data collection to update.
   * @maxLength 256
   */
  dataCollectionId: string;
}

export interface DeleteMultilingualPluginResponse {
  /** Updated data collection. */
  dataCollection?: DataCollection;
}

export interface AddCmsPluginRequest {
  /**
   * ID of the data collection to update.
   * @maxLength 256
   */
  dataCollectionId: string;
  /** CMS plugin options. */
  cmsOptions: PluginCmsOptions;
}

export interface AddCmsPluginResponse {
  /** Updated data collection. */
  dataCollection?: DataCollection;
}

export interface DeleteCmsPluginRequest {
  /**
   * ID of the data collection to update.
   * @maxLength 256
   */
  dataCollectionId: string;
}

export interface DeleteCmsPluginResponse {
  /** Updated data collection. */
  dataCollection?: DataCollection;
}

export interface AddCatalogPluginRequest {
  /**
   * ID of the data collection to update.
   * @maxLength 256
   */
  dataCollectionId: string;
  /** Catalog plugin options. */
  catalogOptions: CatalogOptions;
}

export interface AddCatalogPluginResponse {
  /** Updated data collection. */
  dataCollection?: DataCollection;
}

export interface DeleteCatalogPluginRequest {
  /**
   * ID of the data collection to update.
   * @maxLength 256
   */
  dataCollectionId: string;
}

export interface DeleteCatalogPluginResponse {
  /** Updated data collection. */
  dataCollection?: DataCollection;
}

export interface AddDraftItemsPluginRequest {
  /**
   * ID of the data collection to update.
   * @maxLength 256
   */
  dataCollectionId: string;
  /** Draft Items plugin options. */
  draftItemsOptions: DraftItemsPluginOptions;
  /** Action to take when the Publish plugin is already installed. */
  publishPluginConflictAction?: PublishPluginConflictActionWithLiterals;
}

/** Behavior to apply when adding the Draft Items plugin to a collection that already has the Publish plugin. */
export enum PublishPluginConflictAction {
  /** Fail if the Publish plugin is already installed. */
  FAIL_WHEN_PUBLISH_INSTALLED = 'FAIL_WHEN_PUBLISH_INSTALLED',
  /** Copy draft items with draft status. If a published version already exists, discard the draft item. */
  COPY_DRAFT_ITEMS_WITH_DRAFT_STATUS = 'COPY_DRAFT_ITEMS_WITH_DRAFT_STATUS',
}

/** @enumType */
export type PublishPluginConflictActionWithLiterals =
  | PublishPluginConflictAction
  | 'FAIL_WHEN_PUBLISH_INSTALLED'
  | 'COPY_DRAFT_ITEMS_WITH_DRAFT_STATUS';

export interface AddDraftItemsPluginResponse {
  /** Updated data collection. */
  dataCollection?: DataCollection;
}

export interface DeleteDraftItemsPluginRequest {
  /**
   * ID of the data collection to update.
   * @maxLength 256
   */
  dataCollectionId: string;
  /** Action to take for existing draft items. */
  draftItemsRemovalAction?: DraftItemsRemovalActionWithLiterals;
}

/** Behavior to apply to existing draft items when removing the Draft Items plugin. */
export enum DraftItemsRemovalAction {
  /** Fail if draft items exist. */
  FAIL_IF_DRAFT_ITEMS_EXIST = 'FAIL_IF_DRAFT_ITEMS_EXIST',
  /** Discard draft items, causing data loss. */
  DISCARD_EXISTING_DRAFT_ITEMS = 'DISCARD_EXISTING_DRAFT_ITEMS',
  /** Publish draft items. This overwrites any currently published items with the same IDs. */
  PUBLISH_EXISTING_DRAFT_ITEMS = 'PUBLISH_EXISTING_DRAFT_ITEMS',
}

/** @enumType */
export type DraftItemsRemovalActionWithLiterals =
  | DraftItemsRemovalAction
  | 'FAIL_IF_DRAFT_ITEMS_EXIST'
  | 'DISCARD_EXISTING_DRAFT_ITEMS'
  | 'PUBLISH_EXISTING_DRAFT_ITEMS';

export interface DeleteDraftItemsPluginResponse {
  /** Updated data collection. */
  dataCollection?: DataCollection;
}

export interface PatchDataCollectionFieldRequest {
  /**
   * ID of the data collection to update.
   * @maxLength 256
   */
  dataCollectionId: string;
  /** Field to update. */
  field: Field;
}

export interface PatchDataCollectionFieldResponse {
  /** Updated data collection. */
  dataCollection?: DataCollection;
}

export interface UpdateDataPermissionsRequest {
  /**
   * ID of data collections to update
   * @maxLength 256
   */
  dataCollectionId?: string;
  /** Data permissions to set */
  dataPermissions?: DataPermissions;
}

export interface UpdateDataPermissionsResponse {
  /** Updated data collection */
  dataCollection?: DataCollection;
}

export interface BulkGetDataCollectionsPageBySnapshotsRequest
  extends BulkGetDataCollectionsPageBySnapshotsRequestTenantModeOptionsOneOf {
  appOptions?: AppOptions;
  /**
   * Ids of schema snapshot
   * @format GUID
   * @minSize 1
   * @maxSize 1000
   */
  snapshotIds?: string[];
  /** Pagination information. */
  paging?: Paging;
  /**
   * Tenant mode
   * Default: META_SITE
   */
  tenantMode?: TenantModeWithLiterals;
}

/** @oneof */
export interface BulkGetDataCollectionsPageBySnapshotsRequestTenantModeOptionsOneOf {
  appOptions?: AppOptions;
}

export enum TenantMode {
  /** MetaSite */
  META_SITE = 'META_SITE',
  /** Account */
  ACCOUNT = 'ACCOUNT',
  /** App */
  APP = 'APP',
}

/** @enumType */
export type TenantModeWithLiterals =
  | TenantMode
  | 'META_SITE'
  | 'ACCOUNT'
  | 'APP';

export interface AppOptions {
  /**
   * App definition id
   * @format GUID
   */
  appDefId?: string;
}

export interface BulkGetDataCollectionsPageBySnapshotsResponse {
  /** List of snapshot collection map */
  snapshotCollections?: SnapshotCollection[];
  /** Paging information. */
  pagingMetadata?: PagingMetadataV2;
}

export interface SnapshotCollection {
  /**
   * snapshot to which collection belongs
   * @format GUID
   */
  snapshotId?: string;
  /** snapshot collection */
  collection?: DataCollection;
  /**
   * snapshot of collection indexes
   * @maxSize 100
   */
  indexes?: Index[];
}

/** An index is a map of a collection's data, organized according to specific fields to increase query speed. */
export interface Index {
  /**
   * ID of the index.
   * @readonly
   * @format GUID
   */
  _id?: string;
  /**
   * Name of the index.
   * @minLength 1
   * @maxLength 128
   */
  name?: string;
  /**
   * Fields for which the index is defined.
   *
   * Max: 3 fields (for a unique index: 1 field)
   * @minSize 1
   * @maxSize 3
   */
  fields?: IndexField[];
  /**
   * Current status of the index.
   * @readonly
   */
  status?: IndexStatusWithLiterals;
  /**
   * Contains details about the reasons for failure when `status` is `FAILED`.
   * @readonly
   */
  failure?: Failure;
  /**
   * Whether the index enforces uniqueness of values in the field for which it's defined.
   * If `true`, the index can have only 1 field.
   *
   * Default: `false`
   */
  unique?: boolean;
  /**
   * Whether the index ignores case.
   *
   * Default: `false`
   */
  caseInsensitive?: boolean;
  /**
   * Source of the index. See index sources in [Index Types](https://dev.wix.com/docs/api-reference/business-solutions/cms/indexes/index-types#index-sources).
   *
   * Default: `USER`
   */
  source?: IndexSourceWithLiterals;
}

/**
 * Order determines how values are ordered in the index. This is important when
 * ordering and/or range querying by indexed fields.
 */
export enum Order {
  ASC = 'ASC',
  DESC = 'DESC',
}

/** @enumType */
export type OrderWithLiterals = Order | 'ASC' | 'DESC';

export interface IndexField {
  /**
   * Path of the field to index. For example: `title` or `options.price`.
   * @minLength 1
   * @maxLength 128
   */
  path?: string;
  /**
   * Sort order for the index. Base on how the data is regularly queried.
   *
   * Default: `ASC`
   */
  order?: OrderWithLiterals;
}

export enum IndexStatus {
  /** Place holder. Never returned by the service. */
  UNKNOWN = 'UNKNOWN',
  /** Index creation is in progress. */
  BUILDING = 'BUILDING',
  /** Index has been successfully created and can be used in queries. */
  ACTIVE = 'ACTIVE',
  /** Index is in the process of being dropped. */
  DROPPING = 'DROPPING',
  /** Index has been dropped successfully. */
  DROPPED = 'DROPPED',
  /** Index creation has failed. */
  FAILED = 'FAILED',
  /** Index contains incorrectly indexed data. */
  INVALID = 'INVALID',
}

/** @enumType */
export type IndexStatusWithLiterals =
  | IndexStatus
  | 'UNKNOWN'
  | 'BUILDING'
  | 'ACTIVE'
  | 'DROPPING'
  | 'DROPPED'
  | 'FAILED'
  | 'INVALID';

export interface Failure {
  /**
   * Error code.
   * - `WDE0112`: Unknown error while building collection index.
   * - `WDE0113`: Duplicate key error while building collection index.
   * - `WDE0114`: Document too large while building collection index.
   * @maxLength 7
   */
  code?: string;
  /**
   * Description of the failure.
   * @maxLength 1024
   */
  description?: string;
  /**
   * ID of the data item that caused the failure.
   * For example, if `unique` is `true`, the ID of an item containing a duplicate value.
   * @maxLength 16000
   */
  itemId?: string | null;
}

export enum IndexSource {
  /**
   * Automatically created by Wix for all collections.
   *
   * System indexes are created for essential attributes, such as ID and creation date.
   * You can't delete them and they don't count towards the index limit.
   */
  SYSTEM = 'SYSTEM',
  /** Created by a Wix user via the API or in the dashboard. */
  USER = 'USER',
  /**
   * Created automatically by Wix based on a Wix user's queries.
   *
   * For large collections, Wix identifies slow-running queries and creates indexes automatically to improve performance.
   * These indexes don't count towards the index limit. They're created dynamically, and may be deleted if they no longer fit
   * the system's parameters. To keep an automatic index permanently, manually create an identical `USER` index.
   */
  AUTO = 'AUTO',
}

/** @enumType */
export type IndexSourceWithLiterals = IndexSource | 'SYSTEM' | 'USER' | 'AUTO';

export interface CreateDataCollectionsSnapshotRequest {}

export interface CreateDataCollectionsSnapshotResponse {
  /**
   * created snapshot ID
   * @format GUID
   */
  snapshotId?: string;
  /** data collections in snapshot */
  snapshotCollections?: DataCollection[];
}

export interface CreateAppCollectionsSnapshotRequest {
  /**
   * Snapshot ID to create
   * @format GUID
   */
  snapshotId?: string;
  /**
   * App def id of the app that owns the snapshot
   * @format GUID
   */
  appDefId?: string;
  /**
   * Collections to include in the snapshot
   * @maxSize 1000
   */
  collections?: CollectionDefinition[];
  /**
   * Whether to remove snapshot with all its collections if it already exists.
   * Existing snapshot must be owned by the same app.
   */
  replaceIfExists?: boolean;
}

export interface CollectionDefinition {
  /** Data collection */
  collection?: DataCollection;
  /**
   * Indexes for the data collection
   * @maxSize 100
   */
  indexes?: Index[];
}

export interface CreateAppCollectionsSnapshotResponse {}

export interface RestoreDataCollectionsFromSnapshotRequest {
  /**
   * snapshot ID to restore
   * @format GUID
   */
  snapshotId?: string;
  /**
   * collection IDs to restore, if empty – all collections would be restored
   * @maxSize 1000
   * @maxLength 256
   * @deprecated
   * @replacedBy restoration_collections
   * @targetRemovalDate 2025-12-31
   */
  dataCollectionIds?: string[];
  /**
   * collection to restore, if empty – all collections would be restored
   * @maxSize 1000
   */
  restorationCollections?: RestorationCollection[];
}

export interface Destination {
  /**
   * Collection id.
   * @minLength 1
   * @maxLength 1000
   */
  dataCollectionId?: string;
  /**
   * Display name. When not specified value is taken from the snapshot.
   * @maxLength 1000
   */
  displayName?: string | null;
}

export interface RestorationCollection {
  /**
   * Collection ID to restore
   * @minLength 1
   * @maxLength 1000
   */
  dataCollectionId?: string;
  /**
   * Destination where to restore the collection.
   * When not specified destination is taken from snapshot.
   */
  destination?: Destination;
}

export interface RestoreDataCollectionsFromSnapshotResponse {
  /** restored collections */
  restoredCollections?: DataCollection[];
}

export interface DeleteDataCollectionsSnapshotRequest {
  /**
   * snapshot ID to delete
   * @format GUID
   */
  snapshotId?: string;
}

export interface DeleteDataCollectionsSnapshotResponse {}

export interface CreateMigratedCollectionsSnapshotRequest {
  /** @format GUID */
  existingSnapshotId?: string;
  /** @maxLength 255 */
  newNamespace?: string;
  /** @maxLength 255 */
  existingNamespace?: string;
}

export interface CreateMigratedCollectionsSnapshotResponse {
  /** @format GUID */
  snapshotId?: string;
}

export interface DomainEvent extends DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  _id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at `16:00` and then again at `16:01`, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
}

/** @oneof */
export interface DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
}

export interface EntityCreatedEvent {
  entity?: string;
}

export interface RestoreInfo {
  deletedDate?: Date | null;
}

export interface EntityUpdatedEvent {
  /**
   * Since platformized APIs only expose PATCH and not PUT we can't assume that the fields sent from the client are the actual diff.
   * This means that to generate a list of changed fields (as opposed to sent fields) one needs to traverse both objects.
   * We don't want to impose this on all developers and so we leave this traversal to the notification recipients which need it.
   */
  currentEntity?: string;
}

export interface EntityDeletedEvent {
  /** Entity that was deleted. */
  deletedEntity?: string | null;
}

export interface ActionEvent {
  body?: string;
}

export interface MessageEnvelope {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Stringify payload. */
  data?: string;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
  /** @readonly */
  identityType?: WebhookIdentityTypeWithLiterals;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
}

export enum WebhookIdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

/** @enumType */
export type WebhookIdentityTypeWithLiterals =
  | WebhookIdentityType
  | 'UNKNOWN'
  | 'ANONYMOUS_VISITOR'
  | 'MEMBER'
  | 'WIX_USER'
  | 'APP';

export interface AccountInfo {
  /**
   * ID of the Wix account associated with the event.
   * @format GUID
   */
  accountId?: string | null;
  /**
   * ID of the parent Wix account. Only included when accountId belongs to a child account.
   * @format GUID
   */
  parentAccountId?: string | null;
  /**
   * ID of the Wix site associated with the event. Only included when the event is tied to a specific site.
   * @format GUID
   */
  siteId?: string | null;
}

/**
 * Creates a new data collection.
 *
 * The request body must include an ID, details for at least 1 field, and a permissions object. If any of these are missing, the collection isn't created.
 * @param collection - Collection details.
 * @public
 * @requiredField collection
 * @requiredField collection._id
 * @requiredField collection.fields.key
 * @requiredField collection.fields.type
 * @requiredField collection.permissions.insert
 * @requiredField collection.permissions.read
 * @requiredField collection.permissions.remove
 * @requiredField collection.permissions.update
 * @param options - Options for creating a data collection.
 * @permissionId WIX_DATA.CREATE_COLLECTION
 * @applicableIdentity APP
 * @returns Details of collection created.
 * @fqn com.wixpress.cloud.data.api.collectionservice.DataCollectionService.CreateDataCollection
 */
export async function createDataCollection(
  collection: NonNullablePaths<
    DataCollection,
    | `_id`
    | `fields.${number}.key`
    | `fields.${number}.type`
    | `permissions.insert`
    | `permissions.read`
    | `permissions.remove`
    | `permissions.update`,
    4
  >
): Promise<
  NonNullablePaths<
    DataCollection,
    | `_id`
    | `collectionType`
    | `defaultDisplayOrder.fieldKey`
    | `defaultDisplayOrder.direction`
    | `capabilities.dataOperations`
    | `capabilities.collectionOperations`
    | `capabilities.indexLimits.regular`
    | `capabilities.indexLimits.unique`
    | `capabilities.indexLimits.total`
    | `fields`
    | `fields.${number}.key`
    | `fields.${number}.type`
    | `fields.${number}.typeMetadata.reference.referencedCollectionId`
    | `fields.${number}.typeMetadata.multiReference.referencedCollectionId`
    | `fields.${number}.typeMetadata.multiReference.referencingFieldKey`
    | `fields.${number}.typeMetadata.multiReference.referencingDisplayName`
    | `fields.${number}.typeMetadata.array.elementType`
    | `fields.${number}.systemField`
    | `fields.${number}.capabilities.sortable`
    | `fields.${number}.encrypted`
    | `fields.${number}.condition.permission`
    | `permissions.insert`
    | `permissions.update`
    | `permissions.remove`
    | `permissions.read`
    | `plugins`
    | `plugins.${number}.publishOptions.defaultStatus`
    | `plugins.${number}.singleItemOptions.singleItemId`
    | `plugins.${number}.urlizedOptions.format`
    | `plugins.${number}.editablePageLinkOptions.isPersisted`
    | `plugins.${number}.editablePageLinkOptions.isMutable`
    | `plugins.${number}.type`
    | `pagingModes`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    collection: collection,
  });

  const reqOpts =
    ambassadorWixDataV2DataCollection.createDataCollection(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)?.collection!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { collection: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['collection']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Retrieves a data collection by ID.
 * @param dataCollectionId - ID of the collection to retrieve.
 * @public
 * @requiredField dataCollectionId
 * @param options - Options for retrieving a data collection.
 * @permissionId WIX_DATA.GET_COLLECTION
 * @applicableIdentity APP
 * @returns Details of the collection requested.
 * @fqn com.wixpress.cloud.data.api.collectionservice.DataCollectionService.GetDataCollection
 */
export async function getDataCollection(
  dataCollectionId: string,
  options?: GetDataCollectionOptions
): Promise<
  NonNullablePaths<
    DataCollection,
    | `_id`
    | `collectionType`
    | `defaultDisplayOrder.fieldKey`
    | `defaultDisplayOrder.direction`
    | `capabilities.dataOperations`
    | `capabilities.collectionOperations`
    | `capabilities.indexLimits.regular`
    | `capabilities.indexLimits.unique`
    | `capabilities.indexLimits.total`
    | `fields`
    | `fields.${number}.key`
    | `fields.${number}.type`
    | `fields.${number}.typeMetadata.reference.referencedCollectionId`
    | `fields.${number}.typeMetadata.multiReference.referencedCollectionId`
    | `fields.${number}.typeMetadata.multiReference.referencingFieldKey`
    | `fields.${number}.typeMetadata.multiReference.referencingDisplayName`
    | `fields.${number}.typeMetadata.array.elementType`
    | `fields.${number}.systemField`
    | `fields.${number}.capabilities.sortable`
    | `fields.${number}.encrypted`
    | `fields.${number}.condition.permission`
    | `permissions.insert`
    | `permissions.update`
    | `permissions.remove`
    | `permissions.read`
    | `plugins`
    | `plugins.${number}.publishOptions.defaultStatus`
    | `plugins.${number}.singleItemOptions.singleItemId`
    | `plugins.${number}.urlizedOptions.format`
    | `plugins.${number}.editablePageLinkOptions.isPersisted`
    | `plugins.${number}.editablePageLinkOptions.isMutable`
    | `plugins.${number}.type`
    | `pagingModes`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    dataCollectionId: dataCollectionId,
    consistentRead: options?.consistentRead,
    includeAllowedDataPermissions: options?.includeAllowedDataPermissions,
    fields: options?.fields,
  });

  const reqOpts = ambassadorWixDataV2DataCollection.getDataCollection(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)?.collection!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          dataCollectionId: '$[0]',
          consistentRead: '$[1].consistentRead',
          includeAllowedDataPermissions: '$[1].includeAllowedDataPermissions',
          fields: '$[1].fields',
        },
        singleArgumentUnchanged: false,
      },
      ['dataCollectionId', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface GetDataCollectionOptions {
  /**
   * Whether to retrieve data from the primary database instance.
   * This decreases performance but ensures data retrieved is up to date even immediately after an update.
   * Learn more about [Wix Data and eventual consistency](https://dev.wix.com/api/rest/wix-data/wix-data/eventual-consistency).
   *
   * Default: `false`
   */
  consistentRead?: boolean;
  /**
   * If set allowed for caller data permissions would be included in the response
   * @internal
   */
  includeAllowedDataPermissions?: boolean;
  /**
   * List of specific field names to return, if empty all fields are returned.
   * Affects all returned collections
   * @maxSize 100
   * @maxLength 1000
   */
  fields?: string[];
}

/**
 * Retrieves a list of all data collections associated with the site or project.
 *
 * By default, the list is ordered by ID in ascending order.
 * @public
 * @param options - Options for retrieving a list of data collections.
 * @permissionId WIX_DATA.LIST_COLLECTIONS
 * @applicableIdentity APP
 * @fqn com.wixpress.cloud.data.api.collectionservice.DataCollectionService.ListDataCollections
 */
export async function listDataCollections(
  options?: ListDataCollectionsOptions
): Promise<
  NonNullablePaths<
    ListDataCollectionsResponse,
    | `collections`
    | `collections.${number}._id`
    | `collections.${number}.collectionType`
    | `collections.${number}.defaultDisplayOrder.fieldKey`
    | `collections.${number}.defaultDisplayOrder.direction`
    | `collections.${number}.capabilities.indexLimits.regular`
    | `collections.${number}.capabilities.indexLimits.unique`
    | `collections.${number}.capabilities.indexLimits.total`
    | `collections.${number}.permissions.insert`
    | `collections.${number}.permissions.update`
    | `collections.${number}.permissions.remove`
    | `collections.${number}.permissions.read`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    sort: options?.sort,
    paging: options?.paging,
    consistentRead: options?.consistentRead,
    includeAllowedDataPermissions: options?.includeAllowedDataPermissions,
    fields: options?.fields,
  });

  const reqOpts =
    ambassadorWixDataV2DataCollection.listDataCollections(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          sort: '$[0].sort',
          paging: '$[0].paging',
          consistentRead: '$[0].consistentRead',
          includeAllowedDataPermissions: '$[0].includeAllowedDataPermissions',
          fields: '$[0].fields',
        },
        singleArgumentUnchanged: false,
      },
      ['options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface ListDataCollectionsOptions {
  /**
   * Defines how collections in the response are sorted.
   *
   * Default: Ordered by ID in ascending order.
   */
  sort?: Sorting;
  /** Pagination information. */
  paging?: Paging;
  /**
   * Whether to retrieve data from the primary database instance.
   * This decreases performance but ensures data retrieved is up to date even immediately after an update.
   * Learn more about [Wix Data and eventual consistency](https://dev.wix.com/api/rest/wix-data/wix-data/eventual-consistency).
   *
   * Default: `false`
   */
  consistentRead?: boolean;
  /**
   * If set allowed for caller data permissions would be included in the response
   * @internal
   */
  includeAllowedDataPermissions?: boolean;
  /**
   * List of specific field names to return, if empty all fields are returned.
   * Affects all returned collections
   * @maxSize 100
   * @maxLength 128
   */
  fields?: string[];
}

/**
 * Updates a data collection.
 *
 * A collection ID, revision number, permissions, and at least 1 field must be provided in the `collection` body parameter.
 * If a collection with that ID exists, and if its current `revision` number matches the one provided, it's updated.
 * Otherwise, the request fails.
 *
 * <blockquote class="important">
 * <strong>Important:</strong>
 *
 * After a collection is updated, it only contains the properties included in the Update Data Collection request.
 * If the existing collection has properties with values and those properties aren't included in the updated collection details,
 * their values are lost.<br/><br/>
 *
 * To update only specific properties without affecting others, use [Patch Data Collection](https://dev.wix.com/docs/api-reference/business-solutions/cms/collection-management/data-collections/patch-data-collection).
 * </blockquote>
 *
 * When a collection is updated, its `updatedDate` changes to the current date and its `revision` increments by 1.
 *
 * To update only properties in a collection's `fields` array, use [Update Data Collection Field](https://dev.wix.com/docs/api-reference/business-solutions/cms/collection-management/data-collections/update-data-collection-field).
 * To only add or remove `plugins`, use [Add Data Collection Plugin](https://dev.wix.com/docs/api-reference/business-solutions/cms/collection-management/data-collections/add-data-collection-plugin) or [Delete Data Collection Plugin](https://dev.wix.com/docs/api-reference/business-solutions/cms/collection-management/data-collections/delete-data-collection-plugin).
 * @param collection - Updated collection details. The existing collection is replaced with this version.
 * @public
 * @requiredField collection
 * @requiredField collection._id
 * @requiredField collection.fields.key
 * @requiredField collection.fields.type
 * @requiredField collection.permissions.insert
 * @requiredField collection.permissions.read
 * @requiredField collection.permissions.remove
 * @requiredField collection.permissions.update
 * @requiredField collection.revision
 * @param options - Options for updating a data collection.
 * @permissionId WIX_DATA.UPDATE_COLLECTION
 * @applicableIdentity APP
 * @returns Updated collection details.
 * @fqn com.wixpress.cloud.data.api.collectionservice.DataCollectionService.UpdateDataCollection
 */
export async function updateDataCollection(
  collection: NonNullablePaths<
    DataCollection,
    | `_id`
    | `fields.${number}.key`
    | `fields.${number}.type`
    | `permissions.insert`
    | `permissions.read`
    | `permissions.remove`
    | `permissions.update`
    | `revision`,
    4
  >
): Promise<
  NonNullablePaths<
    DataCollection,
    | `_id`
    | `collectionType`
    | `defaultDisplayOrder.fieldKey`
    | `defaultDisplayOrder.direction`
    | `capabilities.dataOperations`
    | `capabilities.collectionOperations`
    | `capabilities.indexLimits.regular`
    | `capabilities.indexLimits.unique`
    | `capabilities.indexLimits.total`
    | `fields`
    | `fields.${number}.key`
    | `fields.${number}.type`
    | `fields.${number}.typeMetadata.reference.referencedCollectionId`
    | `fields.${number}.typeMetadata.multiReference.referencedCollectionId`
    | `fields.${number}.typeMetadata.multiReference.referencingFieldKey`
    | `fields.${number}.typeMetadata.multiReference.referencingDisplayName`
    | `fields.${number}.typeMetadata.array.elementType`
    | `fields.${number}.systemField`
    | `fields.${number}.capabilities.sortable`
    | `fields.${number}.encrypted`
    | `fields.${number}.condition.permission`
    | `permissions.insert`
    | `permissions.update`
    | `permissions.remove`
    | `permissions.read`
    | `plugins`
    | `plugins.${number}.publishOptions.defaultStatus`
    | `plugins.${number}.singleItemOptions.singleItemId`
    | `plugins.${number}.urlizedOptions.format`
    | `plugins.${number}.editablePageLinkOptions.isPersisted`
    | `plugins.${number}.editablePageLinkOptions.isMutable`
    | `plugins.${number}.type`
    | `pagingModes`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    collection: collection,
  });

  const reqOpts =
    ambassadorWixDataV2DataCollection.updateDataCollection(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)?.collection!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { collection: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['collection']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Deletes a data collection.
 *
 * Before deleting a collection:
 * - Make sure the collection isn't connected to any site pages or elements.
 * - You can create a manual backup of a collections with the Backups API, in case you need to restore them later.
 * - You can also export a collection to a CSV file with the Data Movement Jobs API, if you want to save the data outside of Wix.
 *
 * > **Note:**
 * > Once a collection is deleted, it can only be restored for a limited amount of time.
 * @param dataCollectionId - ID of the collection to delete.
 * @public
 * @requiredField dataCollectionId
 * @permissionId WIX_DATA.DELETE_COLLECTION
 * @applicableIdentity APP
 * @fqn com.wixpress.cloud.data.api.collectionservice.DataCollectionService.DeleteDataCollection
 */
export async function deleteDataCollection(
  dataCollectionId: string
): Promise<void> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    dataCollectionId: dataCollectionId,
  });

  const reqOpts =
    ambassadorWixDataV2DataCollection.deleteDataCollection(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { dataCollectionId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['dataCollectionId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Restores recently deleted collection.
 *
 * Fails with `WDE0025` if collection was deleted too long ago or has never existed.
 * @param dataCollectionId - Data Collection ID to restore
 * @internal
 * @documentationMaturity preview
 * @requiredField dataCollectionId
 * @permissionId WIX_DATA.CREATE_COLLECTION
 * @applicableIdentity APP
 * @fqn com.wixpress.cloud.data.api.collectionservice.DataCollectionService.RestoreDataCollection
 */
export async function restoreDataCollection(
  dataCollectionId: string
): Promise<
  NonNullablePaths<
    RestoreDataCollectionResponse,
    | `dataCollection._id`
    | `dataCollection.collectionType`
    | `dataCollection.defaultDisplayOrder.fieldKey`
    | `dataCollection.defaultDisplayOrder.direction`
    | `dataCollection.capabilities.dataOperations`
    | `dataCollection.capabilities.collectionOperations`
    | `dataCollection.capabilities.indexLimits.regular`
    | `dataCollection.capabilities.indexLimits.unique`
    | `dataCollection.capabilities.indexLimits.total`
    | `dataCollection.fields`
    | `dataCollection.fields.${number}.key`
    | `dataCollection.fields.${number}.type`
    | `dataCollection.fields.${number}.typeMetadata.reference.referencedCollectionId`
    | `dataCollection.fields.${number}.typeMetadata.multiReference.referencedCollectionId`
    | `dataCollection.fields.${number}.typeMetadata.multiReference.referencingFieldKey`
    | `dataCollection.fields.${number}.typeMetadata.multiReference.referencingDisplayName`
    | `dataCollection.fields.${number}.typeMetadata.array.elementType`
    | `dataCollection.fields.${number}.systemField`
    | `dataCollection.fields.${number}.capabilities.sortable`
    | `dataCollection.fields.${number}.encrypted`
    | `dataCollection.fields.${number}.condition.permission`
    | `dataCollection.permissions.insert`
    | `dataCollection.permissions.update`
    | `dataCollection.permissions.remove`
    | `dataCollection.permissions.read`
    | `dataCollection.plugins`
    | `dataCollection.plugins.${number}.publishOptions.defaultStatus`
    | `dataCollection.plugins.${number}.singleItemOptions.singleItemId`
    | `dataCollection.plugins.${number}.urlizedOptions.format`
    | `dataCollection.plugins.${number}.editablePageLinkOptions.isPersisted`
    | `dataCollection.plugins.${number}.editablePageLinkOptions.isMutable`
    | `dataCollection.plugins.${number}.type`
    | `dataCollection.pagingModes`,
    7
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    dataCollectionId: dataCollectionId,
  });

  const reqOpts =
    ambassadorWixDataV2DataCollection.restoreDataCollection(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { dataCollectionId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['dataCollectionId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Updates specified properties of a data collection.
 *
 * Only updates the properties specified in the request, all other properties remain unchanged.
 * The following properties can be updated with this method: `displayName`, `displayField`, and `permissions`.
 * Attempting to update unsupported properties causes this operation to fail.
 *
 * To update properties in a collection's `fields` array, use [Patch Data Collection Field](https://dev.wix.com/docs/api-reference/business-solutions/cms/collection-management/data-collections/patch-data-collection-field).
 * To add or remove `plugins`, use [Add Data Collection Plugin](https://dev.wix.com/docs/api-reference/business-solutions/cms/collection-management/data-collections/add-data-collection-plugin) or [Delete Data Collection Plugin](https://dev.wix.com/docs/api-reference/business-solutions/cms/collection-management/data-collections/delete-data-collection-plugin).
 * @param _id - [Collection ID](https://dev.wix.com/docs/develop-websites-sdk/code-your-site/work-with-data/introduction/about-collections#collection-ids). For example, `my-first-collection`.
 * For [app collections](https://dev.wix.com/docs/api-reference/business-solutions/cms/collection-management/wix-app-collections/introduction), use `AppName/CollectionName`.
 * @public
 * @requiredField _id
 * @requiredField dataCollection
 * @permissionId WIX_DATA.UPDATE_COLLECTION
 * @applicableIdentity APP
 * @fqn com.wixpress.cloud.data.api.collectionservice.DataCollectionService.PatchDataCollection
 */
export async function patchDataCollection(
  _id: string,
  dataCollection: PatchDataCollection
): Promise<
  NonNullablePaths<
    PatchDataCollectionResponse,
    | `dataCollection._id`
    | `dataCollection.collectionType`
    | `dataCollection.defaultDisplayOrder.fieldKey`
    | `dataCollection.defaultDisplayOrder.direction`
    | `dataCollection.capabilities.dataOperations`
    | `dataCollection.capabilities.collectionOperations`
    | `dataCollection.capabilities.indexLimits.regular`
    | `dataCollection.capabilities.indexLimits.unique`
    | `dataCollection.capabilities.indexLimits.total`
    | `dataCollection.fields`
    | `dataCollection.fields.${number}.key`
    | `dataCollection.fields.${number}.type`
    | `dataCollection.fields.${number}.typeMetadata.reference.referencedCollectionId`
    | `dataCollection.fields.${number}.typeMetadata.multiReference.referencedCollectionId`
    | `dataCollection.fields.${number}.typeMetadata.multiReference.referencingFieldKey`
    | `dataCollection.fields.${number}.typeMetadata.multiReference.referencingDisplayName`
    | `dataCollection.fields.${number}.typeMetadata.array.elementType`
    | `dataCollection.fields.${number}.systemField`
    | `dataCollection.fields.${number}.capabilities.sortable`
    | `dataCollection.fields.${number}.encrypted`
    | `dataCollection.fields.${number}.condition.permission`
    | `dataCollection.permissions.insert`
    | `dataCollection.permissions.update`
    | `dataCollection.permissions.remove`
    | `dataCollection.permissions.read`
    | `dataCollection.plugins`
    | `dataCollection.plugins.${number}.publishOptions.defaultStatus`
    | `dataCollection.plugins.${number}.singleItemOptions.singleItemId`
    | `dataCollection.plugins.${number}.urlizedOptions.format`
    | `dataCollection.plugins.${number}.editablePageLinkOptions.isPersisted`
    | `dataCollection.plugins.${number}.editablePageLinkOptions.isMutable`
    | `dataCollection.plugins.${number}.type`
    | `dataCollection.pagingModes`,
    7
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    dataCollection: { ...dataCollection, id: _id },
  });

  const reqOpts =
    ambassadorWixDataV2DataCollection.patchDataCollection(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: { dataCollection: '$[1]' },
        explicitPathsToArguments: { 'dataCollection.id': '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['_id', 'dataCollection']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface PatchDataCollection {
  /**
   * [Collection ID](https://dev.wix.com/docs/develop-websites-sdk/code-your-site/work-with-data/introduction/about-collections#collection-ids). For example, `my-first-collection`.
   * For [app collections](https://dev.wix.com/docs/api-reference/business-solutions/cms/collection-management/wix-app-collections/introduction), use `AppName/CollectionName`.
   * @immutable
   * @maxLength 256
   */
  _id?: string;
  /**
   * Collection type. Indicates how the collection was created and how it is stored.
   * @readonly
   */
  collectionType?: CollectionTypeWithLiterals;
  /**
   * ID of the app that defined this collection. For collections defined by Wix users, this value is null.
   * @format GUID
   * @readonly
   */
  ownerAppId?: string | null;
  /**
   * Maximum number of items returned in a single query, based on the underlying storage.
   * Native collections have a maximum page size of 1000 for offset-based queries or 100 for cursor-based queries.
   * External collections' maximum page size defaults to 50, but an external provider can set any maximum value up to 1000.
   * @readonly
   */
  maxPageSize?: number | null;
  /**
   * Collection's display name as shown in the CMS. For example, `My First Collection`.
   * @maxLength 1000
   */
  displayName?: string | null;
  /**
   * Default item sorting order when a query doesn't specify one.
   * @readonly
   */
  defaultDisplayOrder?: Sort;
  /**
   * UI-friendly namespace of the Wix app with which the data collection is associated, such as Stores or Bookings.
   * Empty for all data collections not owned by Wix apps.
   * @readonly
   * @maxLength 1000
   */
  displayNamespace?: string | null;
  /**
   * Field whose value the CMS displays to represent the collection item when referenced in a different collection.
   * @maxLength 1000
   */
  displayField?: string | null;
  /**
   * Capabilities the collection supports.
   * Capabilities are set by default. Values depend on the collection type.
   * @readonly
   */
  capabilities?: CollectionCapabilities;
  /**
   * Collection's field structure.
   * @maxSize 1000
   */
  fields?: Field[];
  /** [Permissions](https://dev.wix.com/docs/api-reference/business-solutions/cms/collection-management/data-collections/introduction#permissions) required to access and modify collection items, defined by the lowest role needed to perform each action. */
  permissions?: Permissions;
  /**
   * Collection's current revision number, which increments each time the collection is updated. For an update operation to succeed, you must specify the latest revision number.
   * @readonly
   */
  revision?: string | null;
  /**
   * Plugins the collection uses. Plugins apply additional capabilities to the collection or extend its functionality.
   * @maxSize 100
   */
  plugins?: Plugin[];
  /**
   * Paging modes the collection supports. In native collections, offset-based paging is supported by default.
   * @readonly
   * @maxSize 2
   */
  pagingModes?: PagingModeWithLiterals[];
  /**
   * Date the collection was created.
   * @readonly
   */
  _createdDate?: Date | null;
  /**
   * Date the collection was last updated.
   * @readonly
   */
  _updatedDate?: Date | null;
  /**
   * [Permissions](https://dev.wix.com/docs/api-reference/business-solutions/cms/collection-management/data-collections/introduction#permissions) required to access and modify collection items, defined by the lowest access level required to perform each action.
   * @internal
   */
  dataPermissions?: DataPermissions;
  /**
   * Actual data operations permitted for the caller. Included only if requested by `include_allowed_data_permissions`
   * @internal
   * @readonly
   */
  allowedDataPermissions?: AllowedDataPermissions;
  /**
   * Free-text description of the collection's purpose and contents.
   * @internal
   * @maxLength 4000
   */
  description?: string | null;
}

/**
 * Adds a new field to the structure of a data collection.
 * @param dataCollectionId - ID of the data collection to update.
 * @public
 * @requiredField dataCollectionId
 * @requiredField options
 * @requiredField options.field
 * @requiredField options.field.key
 * @requiredField options.field.type
 * @permissionId WIX_DATA.CREATE_COLLECTION_FIELD
 * @applicableIdentity APP
 * @fqn com.wixpress.cloud.data.api.collectionservice.DataCollectionService.CreateDataCollectionField
 */
export async function createDataCollectionField(
  dataCollectionId: string,
  options: NonNullablePaths<
    CreateDataCollectionFieldOptions,
    `field` | `field.key` | `field.type`,
    3
  >
): Promise<
  NonNullablePaths<
    CreateDataCollectionFieldResponse,
    | `dataCollection._id`
    | `dataCollection.collectionType`
    | `dataCollection.defaultDisplayOrder.fieldKey`
    | `dataCollection.defaultDisplayOrder.direction`
    | `dataCollection.capabilities.dataOperations`
    | `dataCollection.capabilities.collectionOperations`
    | `dataCollection.capabilities.indexLimits.regular`
    | `dataCollection.capabilities.indexLimits.unique`
    | `dataCollection.capabilities.indexLimits.total`
    | `dataCollection.fields`
    | `dataCollection.fields.${number}.key`
    | `dataCollection.fields.${number}.type`
    | `dataCollection.fields.${number}.typeMetadata.reference.referencedCollectionId`
    | `dataCollection.fields.${number}.typeMetadata.multiReference.referencedCollectionId`
    | `dataCollection.fields.${number}.typeMetadata.multiReference.referencingFieldKey`
    | `dataCollection.fields.${number}.typeMetadata.multiReference.referencingDisplayName`
    | `dataCollection.fields.${number}.typeMetadata.array.elementType`
    | `dataCollection.fields.${number}.systemField`
    | `dataCollection.fields.${number}.capabilities.sortable`
    | `dataCollection.fields.${number}.encrypted`
    | `dataCollection.fields.${number}.condition.permission`
    | `dataCollection.permissions.insert`
    | `dataCollection.permissions.update`
    | `dataCollection.permissions.remove`
    | `dataCollection.permissions.read`
    | `dataCollection.plugins`
    | `dataCollection.plugins.${number}.publishOptions.defaultStatus`
    | `dataCollection.plugins.${number}.singleItemOptions.singleItemId`
    | `dataCollection.plugins.${number}.urlizedOptions.format`
    | `dataCollection.plugins.${number}.editablePageLinkOptions.isPersisted`
    | `dataCollection.plugins.${number}.editablePageLinkOptions.isMutable`
    | `dataCollection.plugins.${number}.type`
    | `dataCollection.pagingModes`,
    7
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    dataCollectionId: dataCollectionId,
    field: options?.field,
  });

  const reqOpts =
    ambassadorWixDataV2DataCollection.createDataCollectionField(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          dataCollectionId: '$[0]',
          field: '$[1].field',
        },
        singleArgumentUnchanged: false,
      },
      ['dataCollectionId', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface CreateDataCollectionFieldOptions {
  /** New field to add. */
  field: Field;
}

/**
 * Updates properties in a [data collection's](https://dev.wix.com/docs/api-reference/business-solutions/cms/collection-management/data-collections/create-data-collection) `fields` array.
 *
 * <blockquote class="important">
 * <strong>Important:</strong>
 *
 * After `fields` is updated, it only contains the properties included in the Update Data Collection Field request.
 * If the existing `fields` array has properties with values and those properties aren't included in the updated details, their values are lost.<br/><br/>
 *
 * To update only specific properties in the array without affecting others, use [Patch Data Collection Field](https://dev.wix.com/docs/api-reference/business-solutions/cms/collection-management/data-collections/patch-data-collection-field).
 * </blockquote>
 * @param dataCollectionId - ID of the data collection to update.
 * @public
 * @requiredField dataCollectionId
 * @requiredField options
 * @requiredField options.field
 * @requiredField options.field.key
 * @requiredField options.field.type
 * @permissionId WIX_DATA.UPDATE_COLLECTION_FIELD
 * @applicableIdentity APP
 * @fqn com.wixpress.cloud.data.api.collectionservice.DataCollectionService.UpdateDataCollectionField
 */
export async function updateDataCollectionField(
  dataCollectionId: string,
  options: NonNullablePaths<
    UpdateDataCollectionFieldOptions,
    `field` | `field.key` | `field.type`,
    3
  >
): Promise<
  NonNullablePaths<
    UpdateDataCollectionFieldResponse,
    | `dataCollection._id`
    | `dataCollection.collectionType`
    | `dataCollection.defaultDisplayOrder.fieldKey`
    | `dataCollection.defaultDisplayOrder.direction`
    | `dataCollection.capabilities.dataOperations`
    | `dataCollection.capabilities.collectionOperations`
    | `dataCollection.capabilities.indexLimits.regular`
    | `dataCollection.capabilities.indexLimits.unique`
    | `dataCollection.capabilities.indexLimits.total`
    | `dataCollection.fields`
    | `dataCollection.fields.${number}.key`
    | `dataCollection.fields.${number}.type`
    | `dataCollection.fields.${number}.typeMetadata.reference.referencedCollectionId`
    | `dataCollection.fields.${number}.typeMetadata.multiReference.referencedCollectionId`
    | `dataCollection.fields.${number}.typeMetadata.multiReference.referencingFieldKey`
    | `dataCollection.fields.${number}.typeMetadata.multiReference.referencingDisplayName`
    | `dataCollection.fields.${number}.typeMetadata.array.elementType`
    | `dataCollection.fields.${number}.systemField`
    | `dataCollection.fields.${number}.capabilities.sortable`
    | `dataCollection.fields.${number}.encrypted`
    | `dataCollection.fields.${number}.condition.permission`
    | `dataCollection.permissions.insert`
    | `dataCollection.permissions.update`
    | `dataCollection.permissions.remove`
    | `dataCollection.permissions.read`
    | `dataCollection.plugins`
    | `dataCollection.plugins.${number}.publishOptions.defaultStatus`
    | `dataCollection.plugins.${number}.singleItemOptions.singleItemId`
    | `dataCollection.plugins.${number}.urlizedOptions.format`
    | `dataCollection.plugins.${number}.editablePageLinkOptions.isPersisted`
    | `dataCollection.plugins.${number}.editablePageLinkOptions.isMutable`
    | `dataCollection.plugins.${number}.type`
    | `dataCollection.pagingModes`,
    7
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    dataCollectionId: dataCollectionId,
    field: options?.field,
  });

  const reqOpts =
    ambassadorWixDataV2DataCollection.updateDataCollectionField(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          dataCollectionId: '$[0]',
          field: '$[1].field',
        },
        singleArgumentUnchanged: false,
      },
      ['dataCollectionId', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface UpdateDataCollectionFieldOptions {
  /** Field to update. */
  field: Field;
}

/**
 * Deletes a field from the structure of a data collection.
 *
 * When a field is deleted, all values associated with that field are also deleted
 * from all items in the collection.
 * @param dataCollectionId - ID of the data collection to update.
 * @public
 * @requiredField dataCollectionId
 * @requiredField options
 * @requiredField options.fieldKey
 * @permissionId WIX_DATA.DELETE_COLLECTION_FIELD
 * @applicableIdentity APP
 * @fqn com.wixpress.cloud.data.api.collectionservice.DataCollectionService.DeleteDataCollectionField
 */
export async function deleteDataCollectionField(
  dataCollectionId: string,
  options: NonNullablePaths<DeleteDataCollectionFieldOptions, `fieldKey`, 2>
): Promise<
  NonNullablePaths<
    DeleteDataCollectionFieldResponse,
    | `dataCollection._id`
    | `dataCollection.collectionType`
    | `dataCollection.defaultDisplayOrder.fieldKey`
    | `dataCollection.defaultDisplayOrder.direction`
    | `dataCollection.capabilities.dataOperations`
    | `dataCollection.capabilities.collectionOperations`
    | `dataCollection.capabilities.indexLimits.regular`
    | `dataCollection.capabilities.indexLimits.unique`
    | `dataCollection.capabilities.indexLimits.total`
    | `dataCollection.fields`
    | `dataCollection.fields.${number}.key`
    | `dataCollection.fields.${number}.type`
    | `dataCollection.fields.${number}.typeMetadata.reference.referencedCollectionId`
    | `dataCollection.fields.${number}.typeMetadata.multiReference.referencedCollectionId`
    | `dataCollection.fields.${number}.typeMetadata.multiReference.referencingFieldKey`
    | `dataCollection.fields.${number}.typeMetadata.multiReference.referencingDisplayName`
    | `dataCollection.fields.${number}.typeMetadata.array.elementType`
    | `dataCollection.fields.${number}.systemField`
    | `dataCollection.fields.${number}.capabilities.sortable`
    | `dataCollection.fields.${number}.encrypted`
    | `dataCollection.fields.${number}.condition.permission`
    | `dataCollection.permissions.insert`
    | `dataCollection.permissions.update`
    | `dataCollection.permissions.remove`
    | `dataCollection.permissions.read`
    | `dataCollection.plugins`
    | `dataCollection.plugins.${number}.publishOptions.defaultStatus`
    | `dataCollection.plugins.${number}.singleItemOptions.singleItemId`
    | `dataCollection.plugins.${number}.urlizedOptions.format`
    | `dataCollection.plugins.${number}.editablePageLinkOptions.isPersisted`
    | `dataCollection.plugins.${number}.editablePageLinkOptions.isMutable`
    | `dataCollection.plugins.${number}.type`
    | `dataCollection.pagingModes`,
    7
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    dataCollectionId: dataCollectionId,
    fieldKey: options?.fieldKey,
  });

  const reqOpts =
    ambassadorWixDataV2DataCollection.deleteDataCollectionField(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          dataCollectionId: '$[0]',
          fieldKey: '$[1].fieldKey',
        },
        singleArgumentUnchanged: false,
      },
      ['dataCollectionId', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface DeleteDataCollectionFieldOptions {
  /**
   * ID of the field to delete.
   * @maxLength 1000
   */
  fieldKey: string;
}

/**
 * Adds a plugin to a data collection.
 *
 * If a plugin of the same type already exists in the collection, this operation fails.
 * @param dataCollectionId - ID of the data collection to update.
 * @public
 * @requiredField dataCollectionId
 * @requiredField options
 * @requiredField options.plugin
 * @requiredField options.plugin.type
 * @permissionId WIX_DATA.UPDATE_COLLECTION
 * @applicableIdentity APP
 * @fqn com.wixpress.cloud.data.api.collectionservice.DataCollectionService.AddDataCollectionPlugin
 */
export async function addDataCollectionPlugin(
  dataCollectionId: string,
  options: NonNullablePaths<
    AddDataCollectionPluginOptions,
    `plugin` | `plugin.type`,
    3
  >
): Promise<
  NonNullablePaths<
    AddDataCollectionPluginResponse,
    | `dataCollection._id`
    | `dataCollection.collectionType`
    | `dataCollection.defaultDisplayOrder.fieldKey`
    | `dataCollection.defaultDisplayOrder.direction`
    | `dataCollection.capabilities.dataOperations`
    | `dataCollection.capabilities.collectionOperations`
    | `dataCollection.capabilities.indexLimits.regular`
    | `dataCollection.capabilities.indexLimits.unique`
    | `dataCollection.capabilities.indexLimits.total`
    | `dataCollection.fields`
    | `dataCollection.fields.${number}.key`
    | `dataCollection.fields.${number}.type`
    | `dataCollection.fields.${number}.typeMetadata.reference.referencedCollectionId`
    | `dataCollection.fields.${number}.typeMetadata.multiReference.referencedCollectionId`
    | `dataCollection.fields.${number}.typeMetadata.multiReference.referencingFieldKey`
    | `dataCollection.fields.${number}.typeMetadata.multiReference.referencingDisplayName`
    | `dataCollection.fields.${number}.typeMetadata.array.elementType`
    | `dataCollection.fields.${number}.systemField`
    | `dataCollection.fields.${number}.capabilities.sortable`
    | `dataCollection.fields.${number}.encrypted`
    | `dataCollection.fields.${number}.condition.permission`
    | `dataCollection.permissions.insert`
    | `dataCollection.permissions.update`
    | `dataCollection.permissions.remove`
    | `dataCollection.permissions.read`
    | `dataCollection.plugins`
    | `dataCollection.plugins.${number}.publishOptions.defaultStatus`
    | `dataCollection.plugins.${number}.singleItemOptions.singleItemId`
    | `dataCollection.plugins.${number}.urlizedOptions.format`
    | `dataCollection.plugins.${number}.editablePageLinkOptions.isPersisted`
    | `dataCollection.plugins.${number}.editablePageLinkOptions.isMutable`
    | `dataCollection.plugins.${number}.type`
    | `dataCollection.pagingModes`,
    7
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    dataCollectionId: dataCollectionId,
    plugin: options?.plugin,
  });

  const reqOpts =
    ambassadorWixDataV2DataCollection.addDataCollectionPlugin(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          dataCollectionId: '$[0]',
          plugin: '$[1].plugin',
        },
        singleArgumentUnchanged: false,
      },
      ['dataCollectionId', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface AddDataCollectionPluginOptions {
  /**
   * Plugin to add to the collection. Must include the plugin type and any type-specific configuration.
   * If a plugin of the same type already exists in the collection, this operation fails.
   */
  plugin: Plugin;
}

/**
 * Removes a plugin from a data collection.
 *
 * Removing a plugin disables its functionality for the collection.
 * @param dataCollectionId - ID of the data collection to update.
 * @public
 * @requiredField dataCollectionId
 * @requiredField options
 * @requiredField options.pluginType
 * @permissionId WIX_DATA.UPDATE_COLLECTION
 * @applicableIdentity APP
 * @fqn com.wixpress.cloud.data.api.collectionservice.DataCollectionService.DeleteDataCollectionPlugin
 */
export async function deleteDataCollectionPlugin(
  dataCollectionId: string,
  options: NonNullablePaths<DeleteDataCollectionPluginOptions, `pluginType`, 2>
): Promise<
  NonNullablePaths<
    DeleteDataCollectionPluginResponse,
    | `dataCollection._id`
    | `dataCollection.collectionType`
    | `dataCollection.defaultDisplayOrder.fieldKey`
    | `dataCollection.defaultDisplayOrder.direction`
    | `dataCollection.capabilities.dataOperations`
    | `dataCollection.capabilities.collectionOperations`
    | `dataCollection.capabilities.indexLimits.regular`
    | `dataCollection.capabilities.indexLimits.unique`
    | `dataCollection.capabilities.indexLimits.total`
    | `dataCollection.fields`
    | `dataCollection.fields.${number}.key`
    | `dataCollection.fields.${number}.type`
    | `dataCollection.fields.${number}.typeMetadata.reference.referencedCollectionId`
    | `dataCollection.fields.${number}.typeMetadata.multiReference.referencedCollectionId`
    | `dataCollection.fields.${number}.typeMetadata.multiReference.referencingFieldKey`
    | `dataCollection.fields.${number}.typeMetadata.multiReference.referencingDisplayName`
    | `dataCollection.fields.${number}.typeMetadata.array.elementType`
    | `dataCollection.fields.${number}.systemField`
    | `dataCollection.fields.${number}.capabilities.sortable`
    | `dataCollection.fields.${number}.encrypted`
    | `dataCollection.fields.${number}.condition.permission`
    | `dataCollection.permissions.insert`
    | `dataCollection.permissions.update`
    | `dataCollection.permissions.remove`
    | `dataCollection.permissions.read`
    | `dataCollection.plugins`
    | `dataCollection.plugins.${number}.publishOptions.defaultStatus`
    | `dataCollection.plugins.${number}.singleItemOptions.singleItemId`
    | `dataCollection.plugins.${number}.urlizedOptions.format`
    | `dataCollection.plugins.${number}.editablePageLinkOptions.isPersisted`
    | `dataCollection.plugins.${number}.editablePageLinkOptions.isMutable`
    | `dataCollection.plugins.${number}.type`
    | `dataCollection.pagingModes`,
    7
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    dataCollectionId: dataCollectionId,
    pluginType: options?.pluginType,
  });

  const reqOpts =
    ambassadorWixDataV2DataCollection.deleteDataCollectionPlugin(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          dataCollectionId: '$[0]',
          pluginType: '$[1].pluginType',
        },
        singleArgumentUnchanged: false,
      },
      ['dataCollectionId', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface DeleteDataCollectionPluginOptions {
  /** Plugin type to remove. */
  pluginType: PluginTypeWithLiterals;
}

/**
 * Adds the Publish plugin to a data collection.
 * @internal
 * @documentationMaturity preview
 * @requiredField dataCollectionId
 * @requiredField options.publishOptions
 * @param dataCollectionId - ID of the data collection to update.
 * @param publishOptions - Publish plugin options.
 * @param options - Options for adding the Publish plugin.
 * @permissionId data:v2:data_collection:add_publish_plugin
 * @fqn com.wixpress.cloud.data.api.collectionservice.DataCollectionService.AddPublishPlugin
 */
export async function addPublishPlugin(
  dataCollectionId: string,
  options?: NonNullablePaths<AddPublishPluginOptions, `publishOptions`, 2>
): Promise<
  NonNullablePaths<
    AddPublishPluginResponse,
    | `dataCollection._id`
    | `dataCollection.collectionType`
    | `dataCollection.defaultDisplayOrder.fieldKey`
    | `dataCollection.defaultDisplayOrder.direction`
    | `dataCollection.capabilities.dataOperations`
    | `dataCollection.capabilities.collectionOperations`
    | `dataCollection.capabilities.indexLimits.regular`
    | `dataCollection.capabilities.indexLimits.unique`
    | `dataCollection.capabilities.indexLimits.total`
    | `dataCollection.fields`
    | `dataCollection.fields.${number}.key`
    | `dataCollection.fields.${number}.type`
    | `dataCollection.fields.${number}.typeMetadata.reference.referencedCollectionId`
    | `dataCollection.fields.${number}.typeMetadata.multiReference.referencedCollectionId`
    | `dataCollection.fields.${number}.typeMetadata.multiReference.referencingFieldKey`
    | `dataCollection.fields.${number}.typeMetadata.multiReference.referencingDisplayName`
    | `dataCollection.fields.${number}.typeMetadata.array.elementType`
    | `dataCollection.fields.${number}.systemField`
    | `dataCollection.fields.${number}.capabilities.sortable`
    | `dataCollection.fields.${number}.encrypted`
    | `dataCollection.fields.${number}.condition.permission`
    | `dataCollection.permissions.insert`
    | `dataCollection.permissions.update`
    | `dataCollection.permissions.remove`
    | `dataCollection.permissions.read`
    | `dataCollection.plugins`
    | `dataCollection.plugins.${number}.publishOptions.defaultStatus`
    | `dataCollection.plugins.${number}.singleItemOptions.singleItemId`
    | `dataCollection.plugins.${number}.urlizedOptions.format`
    | `dataCollection.plugins.${number}.editablePageLinkOptions.isPersisted`
    | `dataCollection.plugins.${number}.editablePageLinkOptions.isMutable`
    | `dataCollection.plugins.${number}.type`
    | `dataCollection.pagingModes`,
    7
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    dataCollectionId: dataCollectionId,
    publishOptions: options?.publishOptions,
    draftItemsPluginConflictAction: options?.draftItemsPluginConflictAction,
  });

  const reqOpts = ambassadorWixDataV2DataCollection.addPublishPlugin(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          dataCollectionId: '$[0]',
          publishOptions: '$[1].publishOptions',
          draftItemsPluginConflictAction: '$[1].draftItemsPluginConflictAction',
        },
        singleArgumentUnchanged: false,
      },
      ['dataCollectionId', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface AddPublishPluginOptions {
  /** Publish plugin options. */
  publishOptions: PublishPluginOptions;
  /** Action to take when the Draft Items plugin is already installed. */
  draftItemsPluginConflictAction?: DraftItemsPluginConflictActionWithLiterals;
}

/**
 * Removes the Publish plugin from a data collection.
 * @param dataCollectionId - ID of the data collection to update.
 * @internal
 * @documentationMaturity preview
 * @requiredField dataCollectionId
 * @permissionId data:v2:data_collection:delete_publish_plugin
 * @fqn com.wixpress.cloud.data.api.collectionservice.DataCollectionService.DeletePublishPlugin
 */
export async function deletePublishPlugin(
  dataCollectionId: string
): Promise<
  NonNullablePaths<
    DeletePublishPluginResponse,
    | `dataCollection._id`
    | `dataCollection.collectionType`
    | `dataCollection.defaultDisplayOrder.fieldKey`
    | `dataCollection.defaultDisplayOrder.direction`
    | `dataCollection.capabilities.dataOperations`
    | `dataCollection.capabilities.collectionOperations`
    | `dataCollection.capabilities.indexLimits.regular`
    | `dataCollection.capabilities.indexLimits.unique`
    | `dataCollection.capabilities.indexLimits.total`
    | `dataCollection.fields`
    | `dataCollection.fields.${number}.key`
    | `dataCollection.fields.${number}.type`
    | `dataCollection.fields.${number}.typeMetadata.reference.referencedCollectionId`
    | `dataCollection.fields.${number}.typeMetadata.multiReference.referencedCollectionId`
    | `dataCollection.fields.${number}.typeMetadata.multiReference.referencingFieldKey`
    | `dataCollection.fields.${number}.typeMetadata.multiReference.referencingDisplayName`
    | `dataCollection.fields.${number}.typeMetadata.array.elementType`
    | `dataCollection.fields.${number}.systemField`
    | `dataCollection.fields.${number}.capabilities.sortable`
    | `dataCollection.fields.${number}.encrypted`
    | `dataCollection.fields.${number}.condition.permission`
    | `dataCollection.permissions.insert`
    | `dataCollection.permissions.update`
    | `dataCollection.permissions.remove`
    | `dataCollection.permissions.read`
    | `dataCollection.plugins`
    | `dataCollection.plugins.${number}.publishOptions.defaultStatus`
    | `dataCollection.plugins.${number}.singleItemOptions.singleItemId`
    | `dataCollection.plugins.${number}.urlizedOptions.format`
    | `dataCollection.plugins.${number}.editablePageLinkOptions.isPersisted`
    | `dataCollection.plugins.${number}.editablePageLinkOptions.isMutable`
    | `dataCollection.plugins.${number}.type`
    | `dataCollection.pagingModes`,
    7
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    dataCollectionId: dataCollectionId,
  });

  const reqOpts =
    ambassadorWixDataV2DataCollection.deletePublishPlugin(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { dataCollectionId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['dataCollectionId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Adds the Urlized plugin to a data collection.
 * @internal
 * @documentationMaturity preview
 * @requiredField dataCollectionId
 * @requiredField options
 * @requiredField options.urlizedOptions
 * @param dataCollectionId - ID of the data collection to update.
 * @param urlizedOptions - Urlized plugin options.
 * @permissionId data:v2:data_collection:add_urlized_plugin
 * @fqn com.wixpress.cloud.data.api.collectionservice.DataCollectionService.AddUrlizedPlugin
 */
export async function addUrlizedPlugin(
  dataCollectionId: string,
  options: NonNullablePaths<AddUrlizedPluginOptions, `urlizedOptions`, 2>
): Promise<
  NonNullablePaths<
    AddUrlizedPluginResponse,
    | `dataCollection._id`
    | `dataCollection.collectionType`
    | `dataCollection.defaultDisplayOrder.fieldKey`
    | `dataCollection.defaultDisplayOrder.direction`
    | `dataCollection.capabilities.dataOperations`
    | `dataCollection.capabilities.collectionOperations`
    | `dataCollection.capabilities.indexLimits.regular`
    | `dataCollection.capabilities.indexLimits.unique`
    | `dataCollection.capabilities.indexLimits.total`
    | `dataCollection.fields`
    | `dataCollection.fields.${number}.key`
    | `dataCollection.fields.${number}.type`
    | `dataCollection.fields.${number}.typeMetadata.reference.referencedCollectionId`
    | `dataCollection.fields.${number}.typeMetadata.multiReference.referencedCollectionId`
    | `dataCollection.fields.${number}.typeMetadata.multiReference.referencingFieldKey`
    | `dataCollection.fields.${number}.typeMetadata.multiReference.referencingDisplayName`
    | `dataCollection.fields.${number}.typeMetadata.array.elementType`
    | `dataCollection.fields.${number}.systemField`
    | `dataCollection.fields.${number}.capabilities.sortable`
    | `dataCollection.fields.${number}.encrypted`
    | `dataCollection.fields.${number}.condition.permission`
    | `dataCollection.permissions.insert`
    | `dataCollection.permissions.update`
    | `dataCollection.permissions.remove`
    | `dataCollection.permissions.read`
    | `dataCollection.plugins`
    | `dataCollection.plugins.${number}.publishOptions.defaultStatus`
    | `dataCollection.plugins.${number}.singleItemOptions.singleItemId`
    | `dataCollection.plugins.${number}.urlizedOptions.format`
    | `dataCollection.plugins.${number}.editablePageLinkOptions.isPersisted`
    | `dataCollection.plugins.${number}.editablePageLinkOptions.isMutable`
    | `dataCollection.plugins.${number}.type`
    | `dataCollection.pagingModes`,
    7
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    dataCollectionId: dataCollectionId,
    urlizedOptions: options?.urlizedOptions,
  });

  const reqOpts = ambassadorWixDataV2DataCollection.addUrlizedPlugin(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          dataCollectionId: '$[0]',
          urlizedOptions: '$[1].urlizedOptions',
        },
        singleArgumentUnchanged: false,
      },
      ['dataCollectionId', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface AddUrlizedPluginOptions {
  /** Urlized plugin options. */
  urlizedOptions: UrlizedPluginOptions;
}

/**
 * Removes the Urlized plugin from a data collection.
 * @param dataCollectionId - ID of the data collection to update.
 * @internal
 * @documentationMaturity preview
 * @requiredField dataCollectionId
 * @permissionId data:v2:data_collection:delete_urlized_plugin
 * @fqn com.wixpress.cloud.data.api.collectionservice.DataCollectionService.DeleteUrlizedPlugin
 */
export async function deleteUrlizedPlugin(
  dataCollectionId: string
): Promise<
  NonNullablePaths<
    DeleteUrlizedPluginResponse,
    | `dataCollection._id`
    | `dataCollection.collectionType`
    | `dataCollection.defaultDisplayOrder.fieldKey`
    | `dataCollection.defaultDisplayOrder.direction`
    | `dataCollection.capabilities.dataOperations`
    | `dataCollection.capabilities.collectionOperations`
    | `dataCollection.capabilities.indexLimits.regular`
    | `dataCollection.capabilities.indexLimits.unique`
    | `dataCollection.capabilities.indexLimits.total`
    | `dataCollection.fields`
    | `dataCollection.fields.${number}.key`
    | `dataCollection.fields.${number}.type`
    | `dataCollection.fields.${number}.typeMetadata.reference.referencedCollectionId`
    | `dataCollection.fields.${number}.typeMetadata.multiReference.referencedCollectionId`
    | `dataCollection.fields.${number}.typeMetadata.multiReference.referencingFieldKey`
    | `dataCollection.fields.${number}.typeMetadata.multiReference.referencingDisplayName`
    | `dataCollection.fields.${number}.typeMetadata.array.elementType`
    | `dataCollection.fields.${number}.systemField`
    | `dataCollection.fields.${number}.capabilities.sortable`
    | `dataCollection.fields.${number}.encrypted`
    | `dataCollection.fields.${number}.condition.permission`
    | `dataCollection.permissions.insert`
    | `dataCollection.permissions.update`
    | `dataCollection.permissions.remove`
    | `dataCollection.permissions.read`
    | `dataCollection.plugins`
    | `dataCollection.plugins.${number}.publishOptions.defaultStatus`
    | `dataCollection.plugins.${number}.singleItemOptions.singleItemId`
    | `dataCollection.plugins.${number}.urlizedOptions.format`
    | `dataCollection.plugins.${number}.editablePageLinkOptions.isPersisted`
    | `dataCollection.plugins.${number}.editablePageLinkOptions.isMutable`
    | `dataCollection.plugins.${number}.type`
    | `dataCollection.pagingModes`,
    7
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    dataCollectionId: dataCollectionId,
  });

  const reqOpts =
    ambassadorWixDataV2DataCollection.deleteUrlizedPlugin(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { dataCollectionId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['dataCollectionId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Adds the Multilingual plugin to a data collection.
 * @internal
 * @documentationMaturity preview
 * @requiredField dataCollectionId
 * @requiredField options
 * @requiredField options.multilingualOptions
 * @param dataCollectionId - ID of the data collection to update.
 * @param multilingualOptions - Multilingual plugin options.
 * @permissionId data:v2:data_collection:add_multilingual_plugin
 * @fqn com.wixpress.cloud.data.api.collectionservice.DataCollectionService.AddMultilingualPlugin
 */
export async function addMultilingualPlugin(
  dataCollectionId: string,
  options: NonNullablePaths<
    AddMultilingualPluginOptions,
    `multilingualOptions`,
    2
  >
): Promise<
  NonNullablePaths<
    AddMultilingualPluginResponse,
    | `dataCollection._id`
    | `dataCollection.collectionType`
    | `dataCollection.defaultDisplayOrder.fieldKey`
    | `dataCollection.defaultDisplayOrder.direction`
    | `dataCollection.capabilities.dataOperations`
    | `dataCollection.capabilities.collectionOperations`
    | `dataCollection.capabilities.indexLimits.regular`
    | `dataCollection.capabilities.indexLimits.unique`
    | `dataCollection.capabilities.indexLimits.total`
    | `dataCollection.fields`
    | `dataCollection.fields.${number}.key`
    | `dataCollection.fields.${number}.type`
    | `dataCollection.fields.${number}.typeMetadata.reference.referencedCollectionId`
    | `dataCollection.fields.${number}.typeMetadata.multiReference.referencedCollectionId`
    | `dataCollection.fields.${number}.typeMetadata.multiReference.referencingFieldKey`
    | `dataCollection.fields.${number}.typeMetadata.multiReference.referencingDisplayName`
    | `dataCollection.fields.${number}.typeMetadata.array.elementType`
    | `dataCollection.fields.${number}.systemField`
    | `dataCollection.fields.${number}.capabilities.sortable`
    | `dataCollection.fields.${number}.encrypted`
    | `dataCollection.fields.${number}.condition.permission`
    | `dataCollection.permissions.insert`
    | `dataCollection.permissions.update`
    | `dataCollection.permissions.remove`
    | `dataCollection.permissions.read`
    | `dataCollection.plugins`
    | `dataCollection.plugins.${number}.publishOptions.defaultStatus`
    | `dataCollection.plugins.${number}.singleItemOptions.singleItemId`
    | `dataCollection.plugins.${number}.urlizedOptions.format`
    | `dataCollection.plugins.${number}.editablePageLinkOptions.isPersisted`
    | `dataCollection.plugins.${number}.editablePageLinkOptions.isMutable`
    | `dataCollection.plugins.${number}.type`
    | `dataCollection.pagingModes`,
    7
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    dataCollectionId: dataCollectionId,
    multilingualOptions: options?.multilingualOptions,
  });

  const reqOpts =
    ambassadorWixDataV2DataCollection.addMultilingualPlugin(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          dataCollectionId: '$[0]',
          multilingualOptions: '$[1].multilingualOptions',
        },
        singleArgumentUnchanged: false,
      },
      ['dataCollectionId', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface AddMultilingualPluginOptions {
  /** Multilingual plugin options. */
  multilingualOptions: MultilingualOptions;
}

/**
 * Removes the Multilingual plugin from a data collection.
 * @param dataCollectionId - ID of the data collection to update.
 * @internal
 * @documentationMaturity preview
 * @requiredField dataCollectionId
 * @permissionId data:v2:data_collection:delete_multilingual_plugin
 * @fqn com.wixpress.cloud.data.api.collectionservice.DataCollectionService.DeleteMultilingualPlugin
 */
export async function deleteMultilingualPlugin(
  dataCollectionId: string
): Promise<
  NonNullablePaths<
    DeleteMultilingualPluginResponse,
    | `dataCollection._id`
    | `dataCollection.collectionType`
    | `dataCollection.defaultDisplayOrder.fieldKey`
    | `dataCollection.defaultDisplayOrder.direction`
    | `dataCollection.capabilities.dataOperations`
    | `dataCollection.capabilities.collectionOperations`
    | `dataCollection.capabilities.indexLimits.regular`
    | `dataCollection.capabilities.indexLimits.unique`
    | `dataCollection.capabilities.indexLimits.total`
    | `dataCollection.fields`
    | `dataCollection.fields.${number}.key`
    | `dataCollection.fields.${number}.type`
    | `dataCollection.fields.${number}.typeMetadata.reference.referencedCollectionId`
    | `dataCollection.fields.${number}.typeMetadata.multiReference.referencedCollectionId`
    | `dataCollection.fields.${number}.typeMetadata.multiReference.referencingFieldKey`
    | `dataCollection.fields.${number}.typeMetadata.multiReference.referencingDisplayName`
    | `dataCollection.fields.${number}.typeMetadata.array.elementType`
    | `dataCollection.fields.${number}.systemField`
    | `dataCollection.fields.${number}.capabilities.sortable`
    | `dataCollection.fields.${number}.encrypted`
    | `dataCollection.fields.${number}.condition.permission`
    | `dataCollection.permissions.insert`
    | `dataCollection.permissions.update`
    | `dataCollection.permissions.remove`
    | `dataCollection.permissions.read`
    | `dataCollection.plugins`
    | `dataCollection.plugins.${number}.publishOptions.defaultStatus`
    | `dataCollection.plugins.${number}.singleItemOptions.singleItemId`
    | `dataCollection.plugins.${number}.urlizedOptions.format`
    | `dataCollection.plugins.${number}.editablePageLinkOptions.isPersisted`
    | `dataCollection.plugins.${number}.editablePageLinkOptions.isMutable`
    | `dataCollection.plugins.${number}.type`
    | `dataCollection.pagingModes`,
    7
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    dataCollectionId: dataCollectionId,
  });

  const reqOpts =
    ambassadorWixDataV2DataCollection.deleteMultilingualPlugin(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { dataCollectionId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['dataCollectionId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Adds the CMS plugin to a data collection.
 * @internal
 * @documentationMaturity preview
 * @requiredField dataCollectionId
 * @requiredField options
 * @requiredField options.cmsOptions
 * @param dataCollectionId - ID of the data collection to update.
 * @param cmsOptions - CMS plugin options.
 * @permissionId data:v2:data_collection:add_cms_plugin
 * @fqn com.wixpress.cloud.data.api.collectionservice.DataCollectionService.AddCmsPlugin
 */
export async function addCmsPlugin(
  dataCollectionId: string,
  options: NonNullablePaths<AddCmsPluginOptions, `cmsOptions`, 2>
): Promise<
  NonNullablePaths<
    AddCmsPluginResponse,
    | `dataCollection._id`
    | `dataCollection.collectionType`
    | `dataCollection.defaultDisplayOrder.fieldKey`
    | `dataCollection.defaultDisplayOrder.direction`
    | `dataCollection.capabilities.dataOperations`
    | `dataCollection.capabilities.collectionOperations`
    | `dataCollection.capabilities.indexLimits.regular`
    | `dataCollection.capabilities.indexLimits.unique`
    | `dataCollection.capabilities.indexLimits.total`
    | `dataCollection.fields`
    | `dataCollection.fields.${number}.key`
    | `dataCollection.fields.${number}.type`
    | `dataCollection.fields.${number}.typeMetadata.reference.referencedCollectionId`
    | `dataCollection.fields.${number}.typeMetadata.multiReference.referencedCollectionId`
    | `dataCollection.fields.${number}.typeMetadata.multiReference.referencingFieldKey`
    | `dataCollection.fields.${number}.typeMetadata.multiReference.referencingDisplayName`
    | `dataCollection.fields.${number}.typeMetadata.array.elementType`
    | `dataCollection.fields.${number}.systemField`
    | `dataCollection.fields.${number}.capabilities.sortable`
    | `dataCollection.fields.${number}.encrypted`
    | `dataCollection.fields.${number}.condition.permission`
    | `dataCollection.permissions.insert`
    | `dataCollection.permissions.update`
    | `dataCollection.permissions.remove`
    | `dataCollection.permissions.read`
    | `dataCollection.plugins`
    | `dataCollection.plugins.${number}.publishOptions.defaultStatus`
    | `dataCollection.plugins.${number}.singleItemOptions.singleItemId`
    | `dataCollection.plugins.${number}.urlizedOptions.format`
    | `dataCollection.plugins.${number}.editablePageLinkOptions.isPersisted`
    | `dataCollection.plugins.${number}.editablePageLinkOptions.isMutable`
    | `dataCollection.plugins.${number}.type`
    | `dataCollection.pagingModes`,
    7
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    dataCollectionId: dataCollectionId,
    cmsOptions: options?.cmsOptions,
  });

  const reqOpts = ambassadorWixDataV2DataCollection.addCmsPlugin(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          dataCollectionId: '$[0]',
          cmsOptions: '$[1].cmsOptions',
        },
        singleArgumentUnchanged: false,
      },
      ['dataCollectionId', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface AddCmsPluginOptions {
  /** CMS plugin options. */
  cmsOptions: PluginCmsOptions;
}

/**
 * Removes the CMS plugin from a data collection.
 * @param dataCollectionId - ID of the data collection to update.
 * @internal
 * @documentationMaturity preview
 * @requiredField dataCollectionId
 * @permissionId data:v2:data_collection:delete_cms_plugin
 * @fqn com.wixpress.cloud.data.api.collectionservice.DataCollectionService.DeleteCmsPlugin
 */
export async function deleteCmsPlugin(
  dataCollectionId: string
): Promise<
  NonNullablePaths<
    DeleteCmsPluginResponse,
    | `dataCollection._id`
    | `dataCollection.collectionType`
    | `dataCollection.defaultDisplayOrder.fieldKey`
    | `dataCollection.defaultDisplayOrder.direction`
    | `dataCollection.capabilities.dataOperations`
    | `dataCollection.capabilities.collectionOperations`
    | `dataCollection.capabilities.indexLimits.regular`
    | `dataCollection.capabilities.indexLimits.unique`
    | `dataCollection.capabilities.indexLimits.total`
    | `dataCollection.fields`
    | `dataCollection.fields.${number}.key`
    | `dataCollection.fields.${number}.type`
    | `dataCollection.fields.${number}.typeMetadata.reference.referencedCollectionId`
    | `dataCollection.fields.${number}.typeMetadata.multiReference.referencedCollectionId`
    | `dataCollection.fields.${number}.typeMetadata.multiReference.referencingFieldKey`
    | `dataCollection.fields.${number}.typeMetadata.multiReference.referencingDisplayName`
    | `dataCollection.fields.${number}.typeMetadata.array.elementType`
    | `dataCollection.fields.${number}.systemField`
    | `dataCollection.fields.${number}.capabilities.sortable`
    | `dataCollection.fields.${number}.encrypted`
    | `dataCollection.fields.${number}.condition.permission`
    | `dataCollection.permissions.insert`
    | `dataCollection.permissions.update`
    | `dataCollection.permissions.remove`
    | `dataCollection.permissions.read`
    | `dataCollection.plugins`
    | `dataCollection.plugins.${number}.publishOptions.defaultStatus`
    | `dataCollection.plugins.${number}.singleItemOptions.singleItemId`
    | `dataCollection.plugins.${number}.urlizedOptions.format`
    | `dataCollection.plugins.${number}.editablePageLinkOptions.isPersisted`
    | `dataCollection.plugins.${number}.editablePageLinkOptions.isMutable`
    | `dataCollection.plugins.${number}.type`
    | `dataCollection.pagingModes`,
    7
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    dataCollectionId: dataCollectionId,
  });

  const reqOpts = ambassadorWixDataV2DataCollection.deleteCmsPlugin(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { dataCollectionId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['dataCollectionId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Adds the Catalog plugin to a data collection.
 * @internal
 * @documentationMaturity preview
 * @requiredField dataCollectionId
 * @requiredField options
 * @requiredField options.catalogOptions
 * @param dataCollectionId - ID of the data collection to update.
 * @param catalogOptions - Catalog plugin options.
 * @permissionId data:v2:data_collection:add_catalog_plugin
 * @fqn com.wixpress.cloud.data.api.collectionservice.DataCollectionService.AddCatalogPlugin
 */
export async function addCatalogPlugin(
  dataCollectionId: string,
  options: NonNullablePaths<AddCatalogPluginOptions, `catalogOptions`, 2>
): Promise<
  NonNullablePaths<
    AddCatalogPluginResponse,
    | `dataCollection._id`
    | `dataCollection.collectionType`
    | `dataCollection.defaultDisplayOrder.fieldKey`
    | `dataCollection.defaultDisplayOrder.direction`
    | `dataCollection.capabilities.dataOperations`
    | `dataCollection.capabilities.collectionOperations`
    | `dataCollection.capabilities.indexLimits.regular`
    | `dataCollection.capabilities.indexLimits.unique`
    | `dataCollection.capabilities.indexLimits.total`
    | `dataCollection.fields`
    | `dataCollection.fields.${number}.key`
    | `dataCollection.fields.${number}.type`
    | `dataCollection.fields.${number}.typeMetadata.reference.referencedCollectionId`
    | `dataCollection.fields.${number}.typeMetadata.multiReference.referencedCollectionId`
    | `dataCollection.fields.${number}.typeMetadata.multiReference.referencingFieldKey`
    | `dataCollection.fields.${number}.typeMetadata.multiReference.referencingDisplayName`
    | `dataCollection.fields.${number}.typeMetadata.array.elementType`
    | `dataCollection.fields.${number}.systemField`
    | `dataCollection.fields.${number}.capabilities.sortable`
    | `dataCollection.fields.${number}.encrypted`
    | `dataCollection.fields.${number}.condition.permission`
    | `dataCollection.permissions.insert`
    | `dataCollection.permissions.update`
    | `dataCollection.permissions.remove`
    | `dataCollection.permissions.read`
    | `dataCollection.plugins`
    | `dataCollection.plugins.${number}.publishOptions.defaultStatus`
    | `dataCollection.plugins.${number}.singleItemOptions.singleItemId`
    | `dataCollection.plugins.${number}.urlizedOptions.format`
    | `dataCollection.plugins.${number}.editablePageLinkOptions.isPersisted`
    | `dataCollection.plugins.${number}.editablePageLinkOptions.isMutable`
    | `dataCollection.plugins.${number}.type`
    | `dataCollection.pagingModes`,
    7
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    dataCollectionId: dataCollectionId,
    catalogOptions: options?.catalogOptions,
  });

  const reqOpts = ambassadorWixDataV2DataCollection.addCatalogPlugin(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          dataCollectionId: '$[0]',
          catalogOptions: '$[1].catalogOptions',
        },
        singleArgumentUnchanged: false,
      },
      ['dataCollectionId', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface AddCatalogPluginOptions {
  /** Catalog plugin options. */
  catalogOptions: CatalogOptions;
}

/**
 * Removes the Catalog plugin from a data collection.
 * @param dataCollectionId - ID of the data collection to update.
 * @internal
 * @documentationMaturity preview
 * @requiredField dataCollectionId
 * @permissionId data:v2:data_collection:delete_catalog_plugin
 * @fqn com.wixpress.cloud.data.api.collectionservice.DataCollectionService.DeleteCatalogPlugin
 */
export async function deleteCatalogPlugin(
  dataCollectionId: string
): Promise<
  NonNullablePaths<
    DeleteCatalogPluginResponse,
    | `dataCollection._id`
    | `dataCollection.collectionType`
    | `dataCollection.defaultDisplayOrder.fieldKey`
    | `dataCollection.defaultDisplayOrder.direction`
    | `dataCollection.capabilities.dataOperations`
    | `dataCollection.capabilities.collectionOperations`
    | `dataCollection.capabilities.indexLimits.regular`
    | `dataCollection.capabilities.indexLimits.unique`
    | `dataCollection.capabilities.indexLimits.total`
    | `dataCollection.fields`
    | `dataCollection.fields.${number}.key`
    | `dataCollection.fields.${number}.type`
    | `dataCollection.fields.${number}.typeMetadata.reference.referencedCollectionId`
    | `dataCollection.fields.${number}.typeMetadata.multiReference.referencedCollectionId`
    | `dataCollection.fields.${number}.typeMetadata.multiReference.referencingFieldKey`
    | `dataCollection.fields.${number}.typeMetadata.multiReference.referencingDisplayName`
    | `dataCollection.fields.${number}.typeMetadata.array.elementType`
    | `dataCollection.fields.${number}.systemField`
    | `dataCollection.fields.${number}.capabilities.sortable`
    | `dataCollection.fields.${number}.encrypted`
    | `dataCollection.fields.${number}.condition.permission`
    | `dataCollection.permissions.insert`
    | `dataCollection.permissions.update`
    | `dataCollection.permissions.remove`
    | `dataCollection.permissions.read`
    | `dataCollection.plugins`
    | `dataCollection.plugins.${number}.publishOptions.defaultStatus`
    | `dataCollection.plugins.${number}.singleItemOptions.singleItemId`
    | `dataCollection.plugins.${number}.urlizedOptions.format`
    | `dataCollection.plugins.${number}.editablePageLinkOptions.isPersisted`
    | `dataCollection.plugins.${number}.editablePageLinkOptions.isMutable`
    | `dataCollection.plugins.${number}.type`
    | `dataCollection.pagingModes`,
    7
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    dataCollectionId: dataCollectionId,
  });

  const reqOpts =
    ambassadorWixDataV2DataCollection.deleteCatalogPlugin(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { dataCollectionId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['dataCollectionId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Adds the Draft Items plugin to a data collection.
 * @internal
 * @documentationMaturity preview
 * @requiredField dataCollectionId
 * @requiredField options.draftItemsOptions
 * @param dataCollectionId - ID of the data collection to update.
 * @param draftItemsOptions - Draft Items plugin options.
 * @param options - Options for adding the Draft Items plugin.
 * @permissionId data:v2:data_collection:add_draft_items_plugin
 * @fqn com.wixpress.cloud.data.api.collectionservice.DataCollectionService.AddDraftItemsPlugin
 */
export async function addDraftItemsPlugin(
  dataCollectionId: string,
  options?: NonNullablePaths<AddDraftItemsPluginOptions, `draftItemsOptions`, 2>
): Promise<
  NonNullablePaths<
    AddDraftItemsPluginResponse,
    | `dataCollection._id`
    | `dataCollection.collectionType`
    | `dataCollection.defaultDisplayOrder.fieldKey`
    | `dataCollection.defaultDisplayOrder.direction`
    | `dataCollection.capabilities.dataOperations`
    | `dataCollection.capabilities.collectionOperations`
    | `dataCollection.capabilities.indexLimits.regular`
    | `dataCollection.capabilities.indexLimits.unique`
    | `dataCollection.capabilities.indexLimits.total`
    | `dataCollection.fields`
    | `dataCollection.fields.${number}.key`
    | `dataCollection.fields.${number}.type`
    | `dataCollection.fields.${number}.typeMetadata.reference.referencedCollectionId`
    | `dataCollection.fields.${number}.typeMetadata.multiReference.referencedCollectionId`
    | `dataCollection.fields.${number}.typeMetadata.multiReference.referencingFieldKey`
    | `dataCollection.fields.${number}.typeMetadata.multiReference.referencingDisplayName`
    | `dataCollection.fields.${number}.typeMetadata.array.elementType`
    | `dataCollection.fields.${number}.systemField`
    | `dataCollection.fields.${number}.capabilities.sortable`
    | `dataCollection.fields.${number}.encrypted`
    | `dataCollection.fields.${number}.condition.permission`
    | `dataCollection.permissions.insert`
    | `dataCollection.permissions.update`
    | `dataCollection.permissions.remove`
    | `dataCollection.permissions.read`
    | `dataCollection.plugins`
    | `dataCollection.plugins.${number}.publishOptions.defaultStatus`
    | `dataCollection.plugins.${number}.singleItemOptions.singleItemId`
    | `dataCollection.plugins.${number}.urlizedOptions.format`
    | `dataCollection.plugins.${number}.editablePageLinkOptions.isPersisted`
    | `dataCollection.plugins.${number}.editablePageLinkOptions.isMutable`
    | `dataCollection.plugins.${number}.type`
    | `dataCollection.pagingModes`,
    7
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    dataCollectionId: dataCollectionId,
    draftItemsOptions: options?.draftItemsOptions,
    publishPluginConflictAction: options?.publishPluginConflictAction,
  });

  const reqOpts =
    ambassadorWixDataV2DataCollection.addDraftItemsPlugin(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          dataCollectionId: '$[0]',
          draftItemsOptions: '$[1].draftItemsOptions',
          publishPluginConflictAction: '$[1].publishPluginConflictAction',
        },
        singleArgumentUnchanged: false,
      },
      ['dataCollectionId', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface AddDraftItemsPluginOptions {
  /** Draft Items plugin options. */
  draftItemsOptions: DraftItemsPluginOptions;
  /** Action to take when the Publish plugin is already installed. */
  publishPluginConflictAction?: PublishPluginConflictActionWithLiterals;
}

/**
 * Removes the Draft Items plugin from a data collection.
 * @param dataCollectionId - ID of the data collection to update.
 * @internal
 * @documentationMaturity preview
 * @requiredField dataCollectionId
 * @param options - Options for removing the Draft Items plugin.
 * @permissionId data:v2:data_collection:delete_draft_items_plugin
 * @fqn com.wixpress.cloud.data.api.collectionservice.DataCollectionService.DeleteDraftItemsPlugin
 */
export async function deleteDraftItemsPlugin(
  dataCollectionId: string,
  options?: DeleteDraftItemsPluginOptions
): Promise<
  NonNullablePaths<
    DeleteDraftItemsPluginResponse,
    | `dataCollection._id`
    | `dataCollection.collectionType`
    | `dataCollection.defaultDisplayOrder.fieldKey`
    | `dataCollection.defaultDisplayOrder.direction`
    | `dataCollection.capabilities.dataOperations`
    | `dataCollection.capabilities.collectionOperations`
    | `dataCollection.capabilities.indexLimits.regular`
    | `dataCollection.capabilities.indexLimits.unique`
    | `dataCollection.capabilities.indexLimits.total`
    | `dataCollection.fields`
    | `dataCollection.fields.${number}.key`
    | `dataCollection.fields.${number}.type`
    | `dataCollection.fields.${number}.typeMetadata.reference.referencedCollectionId`
    | `dataCollection.fields.${number}.typeMetadata.multiReference.referencedCollectionId`
    | `dataCollection.fields.${number}.typeMetadata.multiReference.referencingFieldKey`
    | `dataCollection.fields.${number}.typeMetadata.multiReference.referencingDisplayName`
    | `dataCollection.fields.${number}.typeMetadata.array.elementType`
    | `dataCollection.fields.${number}.systemField`
    | `dataCollection.fields.${number}.capabilities.sortable`
    | `dataCollection.fields.${number}.encrypted`
    | `dataCollection.fields.${number}.condition.permission`
    | `dataCollection.permissions.insert`
    | `dataCollection.permissions.update`
    | `dataCollection.permissions.remove`
    | `dataCollection.permissions.read`
    | `dataCollection.plugins`
    | `dataCollection.plugins.${number}.publishOptions.defaultStatus`
    | `dataCollection.plugins.${number}.singleItemOptions.singleItemId`
    | `dataCollection.plugins.${number}.urlizedOptions.format`
    | `dataCollection.plugins.${number}.editablePageLinkOptions.isPersisted`
    | `dataCollection.plugins.${number}.editablePageLinkOptions.isMutable`
    | `dataCollection.plugins.${number}.type`
    | `dataCollection.pagingModes`,
    7
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    dataCollectionId: dataCollectionId,
    draftItemsRemovalAction: options?.draftItemsRemovalAction,
  });

  const reqOpts =
    ambassadorWixDataV2DataCollection.deleteDraftItemsPlugin(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          dataCollectionId: '$[0]',
          draftItemsRemovalAction: '$[1].draftItemsRemovalAction',
        },
        singleArgumentUnchanged: false,
      },
      ['dataCollectionId', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface DeleteDraftItemsPluginOptions {
  /** Action to take for existing draft items. */
  draftItemsRemovalAction?: DraftItemsRemovalActionWithLiterals;
}

/**
 * Updates specified properties  in a [data collection's](https://dev.wix.com/docs/api-reference/business-solutions/cms/collection-management/data-collections/create-data-collection) `fields` array.
 *
 * When you change a field's type, existing values aren't migrated. Use [Bulk Update Data Items](https://dev.wix.com/docs/rest/business-solutions/cms/data-items/bulk-update-data-items) or [Bulk Patch Data Items](https://dev.wix.com/docs/rest/business-solutions/cms/data-items/bulk-patch-data-items) to update item values to match the new type.
 *
 * To update properties in a data collection other than `fields`, use [Patch Data Collection](https://dev.wix.com/docs/api-reference/business-solutions/cms/collection-management/data-collections/patch-data-collection).
 * @param dataCollectionId - ID of the data collection to update.
 * @param field - Field to update.
 * @public
 * @requiredField dataCollectionId
 * @requiredField field
 * @permissionId WIX_DATA.UPDATE_COLLECTION_FIELD
 * @applicableIdentity APP
 * @fqn com.wixpress.cloud.data.api.collectionservice.DataCollectionService.PatchDataCollectionField
 */
export async function patchDataCollectionField(
  dataCollectionId: string,
  field: Field
): Promise<
  NonNullablePaths<
    PatchDataCollectionFieldResponse,
    | `dataCollection._id`
    | `dataCollection.collectionType`
    | `dataCollection.defaultDisplayOrder.fieldKey`
    | `dataCollection.defaultDisplayOrder.direction`
    | `dataCollection.capabilities.dataOperations`
    | `dataCollection.capabilities.collectionOperations`
    | `dataCollection.capabilities.indexLimits.regular`
    | `dataCollection.capabilities.indexLimits.unique`
    | `dataCollection.capabilities.indexLimits.total`
    | `dataCollection.fields`
    | `dataCollection.fields.${number}.key`
    | `dataCollection.fields.${number}.type`
    | `dataCollection.fields.${number}.typeMetadata.reference.referencedCollectionId`
    | `dataCollection.fields.${number}.typeMetadata.multiReference.referencedCollectionId`
    | `dataCollection.fields.${number}.typeMetadata.multiReference.referencingFieldKey`
    | `dataCollection.fields.${number}.typeMetadata.multiReference.referencingDisplayName`
    | `dataCollection.fields.${number}.typeMetadata.array.elementType`
    | `dataCollection.fields.${number}.systemField`
    | `dataCollection.fields.${number}.capabilities.sortable`
    | `dataCollection.fields.${number}.encrypted`
    | `dataCollection.fields.${number}.condition.permission`
    | `dataCollection.permissions.insert`
    | `dataCollection.permissions.update`
    | `dataCollection.permissions.remove`
    | `dataCollection.permissions.read`
    | `dataCollection.plugins`
    | `dataCollection.plugins.${number}.publishOptions.defaultStatus`
    | `dataCollection.plugins.${number}.singleItemOptions.singleItemId`
    | `dataCollection.plugins.${number}.urlizedOptions.format`
    | `dataCollection.plugins.${number}.editablePageLinkOptions.isPersisted`
    | `dataCollection.plugins.${number}.editablePageLinkOptions.isMutable`
    | `dataCollection.plugins.${number}.type`
    | `dataCollection.pagingModes`,
    7
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    dataCollectionId: dataCollectionId,
    field: field,
  });

  const reqOpts =
    ambassadorWixDataV2DataCollection.patchDataCollectionField(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { dataCollectionId: '$[0]', field: '$[1]' },
        singleArgumentUnchanged: false,
      },
      ['dataCollectionId', 'field']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}
